import React, { useEffect, useState, useCallback, useMemo } from "react";
import { useNavigate, useParams, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import axiosWithAuth from "../../utils/axiosWithAuth";
import axios from "axios";
import { getApiEndpoint } from "../../utils/apiConfig";
import DirectBreakdownSave from "../../DirectSave";
import Header from "../../components/Header";
import './Maintenance.css';
import { toast } from "react-toastify";

const Maintenance = () => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  let { id } = useParams();
  
  // Check for redirect parameter in URL
  const queryParams = new URLSearchParams(location.search);
  const redirectParam = queryParams.get('redirect');

  document.title = t("maintenance.0");
  const headerTitle = t("maintenance.0");
  const backwardNavigate = "/material";
  
  // Repair info state
  const [breakdown, setBreakdown] = useState(null);
  const [materialPrice, setMaterialPrice] = useState({});
  
  // Repair data loading and error handling
  const [error, setError] = useState(null);
  const [notFound, setNotFound] = useState(false);
  
  const [isLoading, setIsLoading] = useState(true);
  const [isTemporary, setIsTemporary] = useState(false);
  const [selectedMaterial, setSelectedMaterial] = useState(null);
  const token = localStorage.getItem("token");
  const selectedRooms = JSON.parse(sessionStorage.getItem("roomValues")) || [];
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [userInfo, setUserInfo] = useState(null);
  const [userData, setUserData] = useState(null);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  
  // State variables for UI
  const [isExpanded, setIsExpanded] = useState(false);
  const [isLoadingDetails, setIsLoadingDetails] = useState(false);
  const [defaultMaterials, setDefaultMaterials] = useState({});
  const [selectedMaterials, setSelectedMaterials] = useState({});
  const [expandedRooms, setExpandedRooms] = useState({});
  const [showSaveSuccess, setShowSaveSuccess] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [savingError, setSavingError] = useState(null);
  const [showSaveConfirm, setShowSaveConfirm] = useState(false);
  const [roomTotals, setRoomTotals] = useState({});
  const [calculatedTotals, setCalculatedTotals] = useState({
    materialsCost: 0,
    serviceCost: 0,
    generalCost: 0
  });
  
  // Store information state
  const [expandedStoreId, setExpandedStoreId] = useState(null);
  const [materialDetails, setMaterialDetails] = useState({});
  const [materialDetailsAvailable, setMaterialDetailsAvailable] = useState(false);
  
  // Geçici hesaplama için localStorage anahtarı
  const storageKey = useMemo(() => {
    return id || `temp-${Date.now()}`;
  }, [id]);
  
  // Varsayılan malzemeler için localStorage anahtarı
  const DEFAULT_MATERIALS_STORAGE_KEY = 'eprorab_default_materials';
  const SELECTED_MATERIALS_STORAGE_KEY = 'eprorab_selected_materials';
  
  // Fetch door and window prices from settings API
  const fetchDoorWindowPrices = async (repairData) => {
    try {
      // Get repair style and tariff IDs
      let styleId = null;
      let tariffId = null;

      // Try to get from repair data
      if (repairData?.repairStyle) {
        styleId = repairData.repairStyle._id || repairData.repairStyle.id || repairData.repairStyle;
      }
      if (repairData?.repairTariff) {
        tariffId = repairData.repairTariff._id || repairData.repairTariff.id || repairData.repairTariff;
      }

      // If not in repair data, try localStorage repairData
      if (!styleId || !tariffId) {
        const storedRepairData = JSON.parse(localStorage.getItem('repairData') || '{}');
        if (storedRepairData.repairStyle) {
          styleId = storedRepairData.repairStyle._id || storedRepairData.repairStyle.id || storedRepairData.repairStyle;
        }
        if (storedRepairData.repairTariff) {
          tariffId = storedRepairData.repairTariff._id || storedRepairData.repairTariff.id || storedRepairData.repairTariff;
        }
      }

      // If still no IDs, try homeData in sessionStorage
      if (!styleId || !tariffId) {
        const homeData = JSON.parse(sessionStorage.getItem('homeData') || '{}');
        if (homeData.repairStyle) {
          styleId = homeData.repairStyle._id || homeData.repairStyle.id || homeData.repairStyle;
        }
        if (homeData.repairTariff) {
          tariffId = homeData.repairTariff._id || homeData.repairTariff.id || homeData.repairTariff;
        }
      }

      console.log('DEBUG - Maintenance: Fetching door/window prices for:', { 
        styleId, 
        tariffId,
        repairStyleFromInput: repairData?.repairStyle ? {
          id: repairData.repairStyle._id || repairData.repairStyle.id || repairData.repairStyle,
          name: repairData.repairStyle.name || 'unknown'
        } : 'not provided',
        repairTariffFromInput: repairData?.repairTariff ? {
          id: repairData.repairTariff._id || repairData.repairTariff.id || repairData.repairTariff,
          name: repairData.repairTariff.name || 'unknown'
        } : 'not provided'
      });

      if (!styleId || !tariffId) {
        console.warn('DEBUG - Maintenance: Missing styleId or tariffId, using fallback values');
        return {
          doorPrice: 200,
          doorServicePrice: 50,
          windowPrice: 300,
          windowServicePrice: 50
        };
      }

      // First try to get prices from settings API
      const settingsResponse = await axios.get(getApiEndpoint('settings/public'));
      console.log('DEBUG - Settings API Response:', settingsResponse.data);

      if (settingsResponse.data && settingsResponse.data.doorPrices && settingsResponse.data.windowPrices) {
        const style = repairData?.repairStyle?.name?.az?.toLowerCase().replace(/[^a-z0-9]/g, '-') || 'modern';
        const tariff = repairData?.repairTariff?.name?.az?.toLowerCase().replace(/[^a-z0-9]/g, '-') || 'ekonom';
        
        // Find the matching door price entry
        const doorPriceEntry = settingsResponse.data.doorPrices.find(p => 
          p.style?.toLowerCase() === style && p.tariff?.toLowerCase() === tariff
        );
        
        // Find the matching window price entry
        const windowPriceEntry = settingsResponse.data.windowPrices.find(p => 
          p.style?.toLowerCase() === style && p.tariff?.toLowerCase() === tariff
        );

        const doorPrice = doorPriceEntry?.price;
        const doorServicePrice = doorPriceEntry?.servicePrice;
        const windowPrice = windowPriceEntry?.price;
        const windowServicePrice = windowPriceEntry?.servicePrice;

        console.log('DEBUG - Found prices in settings:', {
          doorPrice,
          doorServicePrice,
          windowPrice,
          windowServicePrice,
          style,
          tariff
        });

        if (doorPrice && doorServicePrice && windowPrice && windowServicePrice) {
          return {
            doorPrice: parseFloat(doorPrice),
            doorServicePrice: parseFloat(doorServicePrice),
            windowPrice: parseFloat(windowPrice),
            windowServicePrice: parseFloat(windowServicePrice)
          };
        }
      }

      // If settings API didn't have the prices, try the prices API
      const pricesResponse = await axios.get(getApiEndpoint('settings/prices'), {
        params: { styleId, tariffId },
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        }
      });

      console.log('DEBUG - Prices API Response:', pricesResponse.data);

      if (pricesResponse.data?.prices) {
        const prices = pricesResponse.data.prices;
        return {
          doorPrice: parseFloat(prices.door?.price) || 200,
          doorServicePrice: parseFloat(prices.door?.servicePrice) || 50,
          windowPrice: parseFloat(prices.window?.price) || 300,
          windowServicePrice: parseFloat(prices.window?.servicePrice) || 50
        };
      }

      // If both APIs fail, use fallback values
      console.warn('DEBUG - No prices found in either API, using fallback values');
      return {
        doorPrice: 200,
        doorServicePrice: 50,
        windowPrice: 300,
        windowServicePrice: 50
      };
    } catch (error) {
      console.error('DEBUG - Failed to fetch door/window prices:', error);
      console.error('DEBUG - Error details:', {
        message: error.message,
        response: error.response?.data,
        status: error.response?.status
      });

      // Return fallback values if API fails
      return {
        doorPrice: 200,
        doorServicePrice: 50,
        windowPrice: 300,
        windowServicePrice: 50
      };
    }
  };
  
  // Fetch material details to get store information
  const fetchMaterialDetails = async (breakdownData) => {
    if (!breakdownData || !breakdownData.breakdown || !breakdownData.breakdown.rooms) {
      console.log("No breakdown data to fetch material details for");
      return {};
    }

    const allMaterialIds = [];
    // Track which room each material belongs to
    const materialRoomMap = new Map();
    
    // Collect all material IDs that need fetching and map them to rooms
    breakdownData.breakdown.rooms.forEach(room => {
      const roomName = room.roomType || room.name;
      
      if (room.materials && Array.isArray(room.materials)) {
        room.materials.forEach(material => {
          const materialId = material.materialId;
          if (materialId && materialId !== 'door-material' && materialId !== 'window-material') {
            // Add to list if not already included
            if (!allMaterialIds.includes(materialId)) {
            allMaterialIds.push(materialId);
          }
            
            // Map this material to its room
            materialRoomMap.set(materialId, {
              roomName: roomName,
              roomId: room.id || room._id,
              isDependent: material.isDependentMaterial || false
            });
            
            // Process dependent materials
            if (material.dependentMaterials && Array.isArray(material.dependentMaterials)) {
              material.dependentMaterials.forEach(depMaterial => {
                const depMaterialId = depMaterial.materialId;
                if (depMaterialId && !allMaterialIds.includes(depMaterialId)) {
                  allMaterialIds.push(depMaterialId);
                }
                
                // Map dependent material to same room as parent
                materialRoomMap.set(depMaterialId, {
                  roomName: roomName,
                  roomId: room.id || room._id,
                  isDependent: true,
                  parentMaterialId: materialId
                });
              });
            }
          }
        });
      }
    });
    
    console.log('Material to room mapping:', Object.fromEntries([...materialRoomMap.entries()].slice(0, 10)));

    console.log(`Fetching details for ${allMaterialIds.length} materials:`, allMaterialIds);
    
    const materialDetails = {};
    const failedMaterials = [];
    
    // Fetch details for each material with error handling
    for (const materialId of allMaterialIds) {
      try {
        const response = await axios.get(
          getApiEndpoint(`materials/${materialId}`), 
          { 
            headers: { 
              'Content-Type': 'application/json',
              'Accept': 'application/json',
              'Accept-Language': i18n.language || 'az'
            }
          }
        );
        
        // Get room information for this material
        const roomInfo = materialRoomMap.get(materialId) || {
          roomName: 'Unknown Room',
          isDependent: false
        };
        
        // Add room information to material details
        materialDetails[materialId] = {
          ...response.data,
          roomInfo: roomInfo
        };
        
        console.log(`Successfully fetched details for material ${materialId} in room ${roomInfo.roomName}:`, {
          stores: response.data.stores,
          storeCount: response.data.stores?.length || 0,
          isDependent: roomInfo.isDependent
        });
      } catch (error) {
        console.error(`Failed to fetch material details for ${materialId}:`, error);
        failedMaterials.push({
          id: materialId,
          error: error.response?.data?.message || 'Material not found'
        });
        
        // Get room information even for failed materials
        const roomInfo = materialRoomMap.get(materialId) || {
          roomName: 'Unknown Room',
          isDependent: false
        };
        
        // Don't throw error, just continue with other materials
        // Add a placeholder for the missing material
        materialDetails[materialId] = {
          id: materialId,
          name: { text: 'Material not found', az: 'Materyal tapılmadı' },
          price: 0,
          servicePrice: 0,
          currency: 'AZN',
          error: true,
          roomInfo: roomInfo
        };
      }
    }
    
    if (failedMaterials.length > 0) {
      console.warn(`Failed to fetch ${failedMaterials.length} materials:`, failedMaterials);
      // Show toast notification about missing materials
      toast.warning(`${failedMaterials.length} material məlumatları tapılmadı. Hesablamalar davam edir.`);
    }
    
    console.log("Final materialDetails state:", materialDetails);
    setMaterialDetails(materialDetails);
    setMaterialDetailsAvailable(Object.keys(materialDetails).length > 0);
    
    return materialDetails;
  };
  
  // Calculate detailed breakdown
  const calculateDetailedBreakdown = useCallback(async (repairData) => {
    try {
      // Prepare request body for breakdown calculation
      const requestBody = {
        rooms: repairData.rooms || [],
        defaultMaterialsStatus: repairData.defaultMaterialsStatus || false,
        homeData: repairData.homeData || {}
      };
      
      console.log('Sending breakdown request with defaultMaterialsStatus:', requestBody.defaultMaterialsStatus);
      console.log('Full request body:', JSON.stringify(requestBody, null, 2));
      
      // Check if we can make API call (if user has token)
      const token = localStorage.getItem('token');
      
      if (token) {
        try {
          // Try API call first if user has token
          const response = await axiosWithAuth().post(
            getApiEndpoint('repairs/calculate-breakdown'), 
            requestBody
          );
          
          console.log('Breakdown calculation result:', response.data);
          
          if (response.data && response.data.success) {
            // Get the breakdown data
            const breakdownData = response.data.breakdown || response.data.data;
            const costs = response.data.costs || {};
            
            console.log('Setting breakdown data:', breakdownData);
            console.log('Setting costs data:', costs);
            
            // Check if fixtures costs are missing or zero from API, and fix them
            let updatedBreakdownData = { ...breakdownData };
            let updatedCosts = { ...costs };
            
            // Get door/window data from homeData if fixtures costs are missing
            if ((!costs.doorMaterialsCost && !costs.doorServiceCost) || 
                (!costs.windowMaterialsCost && !costs.windowServiceCost)) {
              
              console.log('Fixtures costs missing from API, calculating manually...');
              
              const homeData = JSON.parse(sessionStorage.getItem("homeData") || '{}');
              
              // Get counts and prices
              const doorCount = homeData.roomDoorCount || repairData.homeData?.totalDoorCount || 0;
              const windowCount = homeData.windowCount || repairData.homeData?.totalWindowCount || 0;
              
              // Fetch door and window prices from settings API instead of using hardcoded values
              const doorWindowPrices = await fetchDoorWindowPrices(repairData);
              const doorPrice = doorWindowPrices.doorPrice;
              const doorServicePrice = doorWindowPrices.doorServicePrice;
              const windowPrice = doorWindowPrices.windowPrice;
              const windowServicePrice = doorWindowPrices.windowServicePrice;
              
              console.log('DEBUG - Using door/window prices from settings API:', {
                doorPrice, doorServicePrice, windowPrice, windowServicePrice,
                source: 'fetchDoorWindowPrices'
              });
              
              // Calculate costs
              const fixtureDoorMaterialCost = doorPrice * doorCount;
              const fixtureDoorServiceCost = doorServicePrice * doorCount;
              const fixtureWindowMaterialCost = windowPrice * windowCount;
              const fixtureWindowServiceCost = windowServicePrice * windowCount;
              
              console.log('Calculated fixture costs:', {
                doorCount, windowCount,
                doorPrice, doorServicePrice, windowPrice, windowServicePrice,
                fixtureDoorMaterialCost, fixtureDoorServiceCost,
                fixtureWindowMaterialCost, fixtureWindowServiceCost
              });
              
              // Update fixtures in breakdown
              if (!updatedBreakdownData.fixtures) {
                updatedBreakdownData.fixtures = {};
              }
              
              updatedBreakdownData.fixtures.doors = {
                count: doorCount,
                price: doorPrice,
                servicePrice: doorServicePrice,
                materialCost: fixtureDoorMaterialCost,
                serviceCost: fixtureDoorServiceCost,
                name: "Doors"
              };
              
              updatedBreakdownData.fixtures.windows = {
                count: windowCount,
                price: windowPrice,
                servicePrice: windowServicePrice,
                materialCost: fixtureWindowMaterialCost,
                serviceCost: fixtureWindowServiceCost,
                name: "Windows"
              };
              
              // Update costs
              updatedCosts.doorMaterialsCost = fixtureDoorMaterialCost;
              updatedCosts.doorServiceCost = fixtureDoorServiceCost;
              updatedCosts.windowMaterialsCost = fixtureWindowMaterialCost;
              updatedCosts.windowServiceCost = fixtureWindowServiceCost;
              
              // Recalculate total costs
              updatedCosts.materialsCost = (updatedCosts.roomMaterialsCost || 0) + 
                                         (updatedCosts.dependentMaterialsCost || 0) + 
                                         fixtureDoorMaterialCost + 
                                         fixtureWindowMaterialCost +
                                         (updatedCosts.globalDefaultMaterialsCost || 0);
                                         
              updatedCosts.serviceCost = (updatedCosts.roomServiceCost || 0) + 
                                       (updatedCosts.dependentServiceCost || 0) + 
                                       fixtureDoorServiceCost + 
                                       fixtureWindowServiceCost +
                                       (updatedCosts.globalDefaultServiceCost || 0);
                                       
              updatedCosts.generalCost = updatedCosts.materialsCost + updatedCosts.serviceCost;
              
              console.log('Updated costs after fixture calculation:', updatedCosts);
            }
            
            // Fetch material details to get store information
            await fetchMaterialDetails({ breakdown: updatedBreakdownData });
            
            setBreakdown(updatedBreakdownData);
            
            // Set material price
            setMaterialPrice({
                materialsCost: updatedCosts.materialsCost || 0,
                serviceCost: updatedCosts.serviceCost || 0,
                generalCost: updatedCosts.generalCost || 0,
                doorMaterialsCost: updatedCosts.doorMaterialsCost || 0,
                windowMaterialsCost: updatedCosts.windowMaterialsCost || 0,
                roomMaterialsCost: updatedCosts.roomMaterialsCost || 0,
                doorServiceCost: updatedCosts.doorServiceCost || 0,
                windowServiceCost: updatedCosts.windowServiceCost || 0,
                roomServiceCost: updatedCosts.roomServiceCost || 0,
                dependentMaterialsCost: updatedCosts.dependentMaterialsCost || 0,
                dependentServiceCost: updatedCosts.dependentServiceCost || 0,
                globalDefaultMaterialsCost: updatedCosts.globalDefaultMaterialsCost || 0,
                globalDefaultServiceCost: updatedCosts.globalDefaultServiceCost || 0,
                currency: updatedCosts.currency || "AZN",
                doorCount: repairData.homeData?.totalDoorCount || 0,
                windowCount: repairData.homeData?.totalWindowCount || 0,
                user: repairData.user || null,
                userInfo: repairData.user?.userName || t("maintenance.anonymousUser"),
                isTemporary: id?.startsWith('temp-') || false
            });
            
            return;
          }
        } catch (apiError) {
          console.error('API calculation failed, falling back to frontend calculation:', apiError);
          // Fall through to frontend calculation
        }
      }
      
      // If API call fails or user doesn't have token, calculate on frontend
      console.log('Calculating breakdown on frontend');
      
      // Check if we have rooms in the repairData.breakdown
      if (repairData.breakdown && repairData.breakdown.rooms && repairData.breakdown.rooms.length > 0) {
        console.log('Using existing breakdown data from repairData:', repairData.breakdown);
        
        // Use the existing breakdown data
        setBreakdown(repairData.breakdown);
        
        // Use the existing costs if available
        if (repairData.costs) {
          console.log('Using existing costs from repairData:', repairData.costs);
          
          setMaterialPrice({
            materialsCost: repairData.costs.materialsCost || 0,
            serviceCost: repairData.costs.serviceCost || 0,
            generalCost: repairData.costs.generalCost || 0,
            doorMaterialsCost: repairData.costs.doorMaterialsCost || 0,
            windowMaterialsCost: repairData.costs.windowMaterialsCost || 0,
            roomMaterialsCost: repairData.costs.roomMaterialsCost || 0,
            doorServiceCost: repairData.costs.doorServiceCost || 0,
            windowServiceCost: repairData.costs.windowServiceCost || 0,
            roomServiceCost: repairData.costs.roomServiceCost || 0,
            dependentMaterialsCost: repairData.costs.dependentMaterialsCost || 0,
            dependentServiceCost: repairData.costs.dependentServiceCost || 0,
            globalDefaultMaterialsCost: repairData.costs.globalDefaultMaterialsCost || 0,
            globalDefaultServiceCost: repairData.costs.globalDefaultServiceCost || 0,
            currency: repairData.costs.currency || "AZN",
            doorCount: repairData.homeData?.totalDoorCount || 0,
            windowCount: repairData.homeData?.totalWindowCount || 0,
            user: repairData.user || null,
            userInfo: repairData.user?.userName || t("maintenance.anonymousUser"),
            isTemporary: id?.startsWith('temp-') || false
          });
          
          // Fetch material details to get store information
          await fetchMaterialDetails({ breakdown: repairData.breakdown });
          
          return;
        }
      }
      
      // Get door/window data from homeData in sessionStorage
      const homeData = JSON.parse(sessionStorage.getItem("homeData") || '{}');
      
      // Get door and window counts from homeData
      const doorCount = homeData.roomDoorCount || repairData.homeData?.totalDoorCount || 0;
      const windowCount = homeData.windowCount || repairData.homeData?.totalWindowCount || 0;
      
      // Fetch door and window prices from settings API
      const doorWindowPrices = await fetchDoorWindowPrices(repairData);
      const { doorPrice, doorServicePrice, windowPrice, windowServicePrice } = doorWindowPrices;
      
      console.log('DEBUG - Door/window prices from API:', doorWindowPrices);
      console.log('DEBUG - Previous hardcoded values were: doorPrice=64, doorServicePrice=24, windowPrice=48, windowServicePrice=16');
      console.log('DEBUG - Now using values from API:', { doorPrice, doorServicePrice, windowPrice, windowServicePrice });
      
      // Calculate door and window costs
      const fixtureDoorMaterialCost = doorPrice * doorCount;
      const fixtureDoorServiceCost = doorServicePrice * doorCount;
      const fixtureWindowMaterialCost = windowPrice * windowCount;
      const fixtureWindowServiceCost = windowServicePrice * windowCount;
      
      const initialFixtures = {
        doors: { 
          count: doorCount, 
          materialCost: fixtureDoorMaterialCost, 
          serviceCost: fixtureDoorServiceCost, 
          name: "Doors",
          price: doorPrice,
          servicePrice: doorServicePrice
        },
        windows: { 
          count: windowCount, 
          materialCost: fixtureWindowMaterialCost, 
          serviceCost: fixtureWindowServiceCost, 
          name: "Windows",
          price: windowPrice,
          servicePrice: windowServicePrice
        }
      };
      
      // Initialize costs
      let materialsCost = 0;
      let serviceCost = 0;
      let doorMaterialsCost = 0;
      let windowMaterialsCost = 0;
      let roomMaterialsCost = 0;
      let doorServiceCost = 0;
      let windowServiceCost = 0;
      let roomServiceCost = 0;
      let dependentMaterialsCost = 0;
      let dependentServiceCost = 0;
      
      // Process rooms and materials
      const rooms = repairData.rooms || [];
      const processedRooms = rooms.map(room => {
        // Calculate room area if not explicitly provided
        let roomArea = parseFloat(room.area || 0);
        
        // If area is invalid or zero, calculate from dimensions
        if (isNaN(roomArea) || roomArea <= 0) {
          const width = parseFloat(room.width || 0);
          const length = parseFloat(room.length || 0);
          roomArea = width * length;
          
          // If still zero, default to 16
          if (isNaN(roomArea) || roomArea <= 0) {
            roomArea = 16;
          }
        }
        
        // Process materials in this room
        let roomTotalCost = 0;
        let roomMaterialCost = 0;
        let roomServiceCost = 0;
        
        const processedMaterials = (room.materials || []).map(material => {
          // Calculate material costs
          const materialPrice = parseFloat(material.price || 0);
          const servicePrice = parseFloat(material.servicePrice || 0);
          const materialArea = parseFloat(material.area || roomArea);
          const quantity = parseFloat(material.quantity || material.count || 1);
          
          const materialCost = materialPrice * quantity * materialArea;
          const materialServiceCost = servicePrice * quantity * materialArea;
          
          // Add to room totals
          roomMaterialCost += materialCost;
          roomServiceCost += materialServiceCost;
          
          // Process dependent materials
          let totalDependentCost = 0;
          let totalDependentServiceCost = 0;
          
          const processedDependentMaterials = (material.dependentMaterials || []).map(dep => {
            const depPrice = parseFloat(dep.price || 0);
            const depServicePrice = parseFloat(dep.servicePrice || 0);
            const depQuantity = parseFloat(dep.quantity || dep.count || 1);
            
            const depCost = depPrice * depQuantity * materialArea;
            const depServiceCost = depServicePrice * depQuantity * materialArea;
            
            totalDependentCost += depCost;
            totalDependentServiceCost += depServiceCost;
            
            return {
              ...dep,
              totalCost: depCost + depServiceCost
            };
          });
          
          // Add dependent costs to global totals
          dependentMaterialsCost += totalDependentCost;
          dependentServiceCost += totalDependentServiceCost;
          
          return {
            ...material,
            totalCost: materialCost + materialServiceCost,
            totalMaterialCost: materialCost,
            totalServiceCost: materialServiceCost,
            dependentMaterials: processedDependentMaterials,
            totalDependentCost,
            totalDependentServiceCost
          };
        });
        
        // Add to global totals
        roomMaterialsCost += roomMaterialCost;
        roomServiceCost += roomServiceCost;
        
        return {
          ...room,
          area: roomArea,
          materials: processedMaterials,
          totalCost: roomMaterialCost + roomServiceCost,
          materialCost: roomMaterialCost,
          serviceCost: roomServiceCost
        };
      });
      
      // Process fixtures
      const fixtures = {
        doors: {
          ...initialFixtures.doors,
          totalCost: (initialFixtures.doors.materialCost || 0) + (initialFixtures.doors.serviceCost || 0)
        },
        windows: {
          ...initialFixtures.windows,
          totalCost: (initialFixtures.windows.materialCost || 0) + (initialFixtures.windows.serviceCost || 0)
        }
      };
      
      // Add fixtures costs to global totals
      doorMaterialsCost = fixtures.doors.materialCost || 0;
      doorServiceCost = fixtures.doors.serviceCost || 0;
      windowMaterialsCost = fixtures.windows.materialCost || 0;
      windowServiceCost = fixtures.windows.serviceCost || 0;
      
      // Calculate total costs
      materialsCost = roomMaterialsCost + doorMaterialsCost + windowMaterialsCost + dependentMaterialsCost;
      serviceCost = roomServiceCost + doorServiceCost + windowServiceCost + dependentServiceCost;
      const generalCost = materialsCost + serviceCost;
      
      // Create breakdown data structure
      const breakdownData = {
        rooms: processedRooms,
        fixtures
      };
      
      // Create costs structure
      const costs = {
        materialsCost,
        serviceCost,
        generalCost,
        doorMaterialsCost,
        windowMaterialsCost,
        roomMaterialsCost,
        doorServiceCost,
        windowServiceCost,
        roomServiceCost,
        dependentMaterialsCost,
        dependentServiceCost,
        currency: "AZN"
      };
      
      // Set state
      setBreakdown(breakdownData);
      setMaterialPrice({
        ...costs,
        doorCount: repairData.homeData?.totalDoorCount || 0,
        windowCount: repairData.homeData?.totalWindowCount || 0,
        user: repairData.user || null,
        userInfo: repairData.user?.userName || t("maintenance.anonymousUser"),
        isTemporary: id?.startsWith('temp-') || false
      });
      
      // Fetch material details to get store information
      await fetchMaterialDetails(breakdownData);
      
    } catch (error) {
      console.error('Error calculating detailed breakdown:', error);
    }
  }, [id, t]);
  
  // localStorage'dan varsayılan malzeme durumlarını yükle
  useEffect(() => {
    try {
      const savedStatus = localStorage.getItem(DEFAULT_MATERIALS_STORAGE_KEY);
      if (savedStatus) {
        setDefaultMaterials(JSON.parse(savedStatus));
      }
    } catch (error) {
      console.error("Error loading default materials status:", error);
    }
  }, []);
  
  // Initialize selected materials and expanded rooms when breakdown data is loaded
  useEffect(() => {
    if (breakdown?.rooms) {
      // Initialize all materials as selected by default
      const initialSelectedMaterials = {};
      // Initialize all rooms as expanded by default
      const initialExpandedRooms = {};
      
      breakdown.rooms.forEach(room => {
        // Set room as expanded by default
        initialExpandedRooms[room.id] = true;
        
        room.materials?.forEach(material => {
          initialSelectedMaterials[material.materialId] = true;
          
          // Also select all dependent materials
          material.dependentMaterials?.forEach(depMaterial => {
            initialSelectedMaterials[depMaterial.materialId] = true;
          });
        });
      });
      
      // If there's no saved selection state, use the initial state
      if (!localStorage.getItem(SELECTED_MATERIALS_STORAGE_KEY)) {
        setSelectedMaterials(initialSelectedMaterials);
        localStorage.setItem(SELECTED_MATERIALS_STORAGE_KEY, JSON.stringify(initialSelectedMaterials));
      } else {
        // Otherwise load from localStorage
        try {
          const savedStatus = localStorage.getItem(SELECTED_MATERIALS_STORAGE_KEY);
          setSelectedMaterials(JSON.parse(savedStatus));
        } catch (error) {
          console.error("Error loading selected materials status:", error);
          setSelectedMaterials(initialSelectedMaterials);
        }
      }
      
      // Set all rooms as expanded by default
      setExpandedRooms(initialExpandedRooms);
    }
  }, [breakdown]);
  
  // Initialize expandedRooms state when breakdown data is loaded
  useEffect(() => {
    if (breakdown?.rooms) {
      const initialExpandedState = {};
      breakdown.rooms.forEach(room => {
        // Set all rooms to collapsed by default
        initialExpandedState[room.id] = false;
      });
      setExpandedRooms(initialExpandedState);
    }
  }, [breakdown]);
  
  // LocalStorage'dan veri alma yardımcı fonksiyonu
  const getStorageData = useCallback(() => {
    try {
      const savedData = localStorage.getItem(storageKey);
      if (savedData) {
        const parsedData = JSON.parse(savedData);
        
        // Veri yapısını kontrol et
        if (typeof parsedData === 'object') {
          if (parsedData.hasOwnProperty('calculationData')) {
            console.log('Storage data already has proper structure');
            return parsedData;
          } else {
            // Eski formattan yeni formata dönüştür
            console.log('Converting old storage format to new structure');
            return {
              calculationData: parsedData,
              defaultMaterialsStatus: JSON.parse(localStorage.getItem(DEFAULT_MATERIALS_STORAGE_KEY) || '{}')
            };
          }
        }
      }
      return { calculationData: null };
    } catch (error) {
      console.error('Error reading from localStorage:', error);
      return { calculationData: null };
    }
  }, [storageKey]);
  
  // Varsayılan malzeme durumunu değiştirme fonksiyonu
  const handleDefaultMaterialChange = useCallback(async (materialId, isChecked) => {
    try {
      // Önceki durumu al
      const currentStatus = { ...defaultMaterials };
      
      // Yeni durumu ayarla
      currentStatus[materialId] = isChecked;
      
      // State'i güncelle
      setDefaultMaterials(currentStatus);
      
      // Güncel durumu localStorage'a kaydet
      localStorage.setItem(DEFAULT_MATERIALS_STORAGE_KEY, JSON.stringify(currentStatus));
      
      console.log('Default materials status updated:', currentStatus);
      
      // Get current repair data directly instead of using storageData
      const repairData = id?.startsWith('temp-') 
        ? JSON.parse(localStorage.getItem(id))
        : JSON.parse(sessionStorage.getItem('currentRepairData'));
      
      if (repairData) {
        const updatedRepairData = {
          ...repairData,
          defaultMaterialsStatus: currentStatus  // Use the updated status directly
        };
        
        console.log('Updating breakdown defaults with updated default materials status:', updatedRepairData);
        
        // Import the new update function
        const { updateBreakdownDefaults } = await import('../../services/repairService');
        
        // Get fresh roomValues from sessionStorage to ensure we have the latest data
        const freshRoomValues = JSON.parse(sessionStorage.getItem('roomValues') || '[]');
        
        // Prepare the request data in the exact format expected by the backend
        const updateRequestData = {
          materialsObj: updatedRepairData.materials,
          property: {
            ...updatedRepairData.homeData,
            roomValues: freshRoomValues
          },
          repairStyleId: updatedRepairData.repairStyle.id,
          repairTariffId: updatedRepairData.repairTariff.id,
          defaultMaterialsStatus: currentStatus
        };
        
        // Use the new PUT endpoint instead of full recalculation
        const updateResult = await updateBreakdownDefaults(updateRequestData);
        
        if (updateResult && updateResult.success) {
          // Update the UI with the new costs
          if (updateResult.costs) {
            // Log the detailed price changes for debugging
            console.log('🔄 PRICE CHANGE LOG:');
            console.log('Toggle State:', defaultMaterials);
            console.log('Material being toggled:', materialId, isChecked ? 'CHECKED' : 'UNCHECKED');
            console.log('Total Material Cost:', updateResult.costs.materialsCost);
            console.log('Total Service Cost:', updateResult.costs.serviceCost);
            console.log('Global Default Material Cost:', updateResult.costs.globalDefaultMaterialsCost);
            console.log('Global Default Service Cost:', updateResult.costs.globalDefaultServiceCost);
            console.log('Total General Cost:', updateResult.costs.generalCost);
            
            // Check if any default materials exist in the breakdown
            const defaultMaterialsInBreakdown = [];
            if (updateResult.breakdown?.rooms) {
              updateResult.breakdown.rooms.forEach(room => {
                if (room.materials && Array.isArray(room.materials)) {
                  room.materials.forEach(material => {
                    if (material.isDefault === true || material.isGlobalDefault === true) {
                      defaultMaterialsInBreakdown.push({
                        materialId: material.materialId || material.id,
                        name: material.name?.az || material.materialName,
                        price: material.price,
                        servicePrice: material.servicePrice,
                        totalArea: material.totalArea || material.area,
                        materialCost: material.price * (material.totalArea || material.area),
                        serviceCost: material.servicePrice * (material.totalArea || material.area)
                      });
                    }
                  });
                }
              });
            }
            
            console.log('Default Materials in Breakdown:', defaultMaterialsInBreakdown);
            console.log('🔄 END PRICE CHANGE LOG');
            
            setMaterialPrice({
              materialsCost: updateResult.costs.materialsCost || 0,
              serviceCost: updateResult.costs.serviceCost || 0,
              generalCost: updateResult.costs.generalCost || 0,
              doorMaterialsCost: updateResult.costs.doorMaterialsCost || 0,
              windowMaterialsCost: updateResult.costs.windowMaterialsCost || 0,
              roomMaterialsCost: updateResult.costs.roomMaterialsCost || 0,
              doorServiceCost: updateResult.costs.doorServiceCost || 0,
              windowServiceCost: updateResult.costs.windowServiceCost || 0,
              roomServiceCost: updateResult.costs.roomServiceCost || 0,
              dependentMaterialsCost: updateResult.costs.dependentMaterialsCost || 0,
              dependentServiceCost: updateResult.costs.dependentServiceCost || 0,
              globalDefaultMaterialsCost: updateResult.costs.globalDefaultMaterialsCost || 0,
              globalDefaultServiceCost: updateResult.costs.globalDefaultServiceCost || 0,
              currency: updateResult.costs.currency || "AZN",
              doorCount: updateResult.property?.doorCount || updatedRepairData.homeData?.totalDoorCount || 0,
              windowCount: updateResult.property?.windowCount || updatedRepairData.homeData?.totalWindowCount || 0,
              user: updatedRepairData.user || null,
              userInfo: updatedRepairData.user?.userName || 'Anonymous User',
              isTemporary: id.startsWith('temp-')
            });
            
            // Update breakdown data
            setBreakdown(updateResult.breakdown);
            
            console.log('Successfully updated prices with new default materials status');
          }
        } else {
          console.error('Failed to update breakdown defaults:', updateResult);
        }
      } else {
        console.error('No repair data found for recalculation');
      }
    } catch (error) {
      console.error('Error updating default material status:', error);
    }
  }, [defaultMaterials, calculateDetailedBreakdown, id]);
  
  // Varsayılan malzemeleri açıp/kapatma fonksiyonu
  const toggleDefaultMaterial = (materialId) => {
    setDefaultMaterials(prev => {
      const newStatus = {
        ...prev,
        [materialId]: !prev[materialId]
      };
      
      // Yeni durumu localStorage'a kaydet
      try {
        localStorage.setItem(DEFAULT_MATERIALS_STORAGE_KEY, JSON.stringify(newStatus));
      } catch (error) {
        console.error("Error saving default materials status:", error);
      }
      
      return newStatus;
    });
    
    // Hesaplamayı yeniden yapacak işlevi çağır
    recalculateWithUpdatedDefaults();
  };
  
  // Toggle material selection and update totals
  const toggleMaterial = (materialId, material, room) => {
    setSelectedMaterials(prev => {
      const newSelectedMaterials = {
        ...prev,
        [materialId]: !prev[materialId]
      };

      // Calculate the price changes
      const isSelected = !prev[materialId];
      const materialArea = parseFloat(material.area || 1);
      const useRoomArea = materialArea === 1;
      const count = parseFloat(material.count || 1);
      const totalArea = useRoomArea ? parseFloat(room.area) * count : materialArea * count;
      
      // Use 1 as fallback only if usagePerSquareMeter is null, undefined, or NaN
      let usagePerSquareMeter = parseFloat(material.usagePerSquareMeter);
      if (isNaN(usagePerSquareMeter)) {
        usagePerSquareMeter = 1;
      }
      
      const materialPrice = parseFloat(material.price || 0);
      const servicePrice = parseFloat(material.servicePrice || 0);
      
      // Material cost: totalArea * usagePerSquareMeter * price
      const actualMaterialUsage = totalArea * usagePerSquareMeter;
      const materialCost = materialPrice * actualMaterialUsage;
      
      // Service cost: totalArea * servicePrice (no usagePerSquareMeter)
      const serviceCost = servicePrice * totalArea;

      // Update calculated totals
      setCalculatedTotals(prev => {
        let newMaterialsCost = prev.materialsCost;
        let newServiceCost = prev.serviceCost;

        if (isSelected) {
          newMaterialsCost += materialCost;
          newServiceCost += serviceCost;

          // Add dependent material costs if this is a parent material
          if (material.dependentMaterials) {
            material.dependentMaterials.forEach(depMaterial => {
              if (depMaterial.status !== false && depMaterial.isActive !== false) {
                const depPrice = parseFloat(depMaterial.price || 0);
                const depServicePrice = parseFloat(depMaterial.servicePrice || 0);
                
                let depUsagePerSquareMeter = parseFloat(depMaterial.usagePerSquareMeter);
                if (isNaN(depUsagePerSquareMeter)) {
                  depUsagePerSquareMeter = 1;
                }
                
                const depActualMaterialUsage = totalArea * depUsagePerSquareMeter;
                const depMaterialCost = depPrice * depActualMaterialUsage;
                const depServiceCost = depServicePrice * totalArea;

                newMaterialsCost += depMaterialCost;
                newServiceCost += depServiceCost;
              }
            });
          }
        } else {
          newMaterialsCost -= materialCost;
          newServiceCost -= serviceCost;

          // Subtract dependent material costs if this is a parent material
          if (material.dependentMaterials) {
            material.dependentMaterials.forEach(depMaterial => {
              if (depMaterial.status !== false && depMaterial.isActive !== false) {
                const depPrice = parseFloat(depMaterial.price || 0);
                const depServicePrice = parseFloat(depMaterial.servicePrice || 0);
                
                let depUsagePerSquareMeter = parseFloat(depMaterial.usagePerSquareMeter);
                if (isNaN(depUsagePerSquareMeter)) {
                  depUsagePerSquareMeter = 1;
                }
                
                const depActualMaterialUsage = totalArea * depUsagePerSquareMeter;
                const depMaterialCost = depPrice * depActualMaterialUsage;
                const depServiceCost = depServicePrice * totalArea;

                newMaterialsCost -= depMaterialCost;
                newServiceCost -= depServiceCost;
              }
            });
          }
        }

        return {
          materialsCost: newMaterialsCost,
          serviceCost: newServiceCost,
          generalCost: newMaterialsCost + newServiceCost
        };
      });

      return newSelectedMaterials;
    });
  };
  
  // Update calculated totals when a material is toggled
  const updateCalculatedTotals = (materialId, material, room) => {
    // Check if material is currently selected
    const isCurrentlySelected = selectedMaterials[materialId];
    
    // Calculate material costs
    const materialArea = parseFloat(material.area || 1);
    const useRoomArea = materialArea === 1;
    const count = parseFloat(material.count || 1);
    const totalArea = useRoomArea ? parseFloat(room.area) * count : materialArea * count;
    
    // Use 1 as fallback only if usagePerSquareMeter is null, undefined, or NaN
    let usagePerSquareMeter = parseFloat(material.usagePerSquareMeter);
    if (isNaN(usagePerSquareMeter)) {
      usagePerSquareMeter = 1;
    }
    
    const materialPrice = parseFloat(material.price || 0);
    const servicePrice = parseFloat(material.servicePrice || 0);
    
    // Material cost: totalArea * usagePerSquareMeter * price
    const actualMaterialUsage = totalArea * usagePerSquareMeter;
    const materialCost = materialPrice * actualMaterialUsage;
    
    // Service cost: totalArea * servicePrice (no usagePerSquareMeter)
    const serviceCost = servicePrice * totalArea;
    
    // Calculate dependent material costs if this is a parent material
    let dependentMaterialsCost = 0;
    let dependentServiceCost = 0;
    
    if (material.dependentMaterials && Array.isArray(material.dependentMaterials)) {
      material.dependentMaterials.forEach(depMaterial => {
        // Skip if dependent material is inactive
        if (depMaterial.status === false || depMaterial.isActive === false) {
          return;
        }
        
        // Calculate dependent material costs
        const depPrice = parseFloat(depMaterial.price || 0);
        const depServicePrice = parseFloat(depMaterial.servicePrice || 0);
        
        // Use 1 as fallback only if usagePerSquareMeter is null, undefined, or NaN
        let depUsagePerSquareMeter = parseFloat(depMaterial.usagePerSquareMeter);
        if (isNaN(depUsagePerSquareMeter)) {
          depUsagePerSquareMeter = 1;
        }
        
        // Dependent material cost: totalArea * usagePerSquareMeter * price
        const depActualMaterialUsage = totalArea * depUsagePerSquareMeter;
        const depMaterialCost = depPrice * depActualMaterialUsage;
        
        // Dependent service cost: totalArea * servicePrice (no usagePerSquareMeter)
        const depServiceCost = depServicePrice * totalArea;
        
        dependentMaterialsCost += depMaterialCost;
        dependentServiceCost += depServiceCost;
      });
    }
    
    // Update totals based on selection state
    setCalculatedTotals(prev => {
      const materialChange = isCurrentlySelected ? -materialCost : materialCost;
      const serviceChange = isCurrentlySelected ? -serviceCost : serviceCost;
      const dependentMaterialChange = isCurrentlySelected ? -dependentMaterialsCost : dependentMaterialsCost;
      const dependentServiceChange = isCurrentlySelected ? -dependentServiceCost : dependentServiceCost;
      
      const newMaterialsCost = Math.max(0, prev.materialsCost + materialChange + dependentMaterialChange);
      const newServiceCost = Math.max(0, prev.serviceCost + serviceChange + dependentServiceChange);
      
      return {
        materialsCost: newMaterialsCost,
        serviceCost: newServiceCost,
        generalCost: newMaterialsCost + newServiceCost
      };
    });
  };
  
  // Varsayılan malzeme durumları değiştiğinde hesaplamayı güncelle
  const recalculateWithUpdatedDefaults = useCallback(async () => {
    // Şimdilik sadece log atalım, gerçek implementasyonu daha sonra ekleyeceğiz
    console.log("Default materials status updated:", defaultMaterials);
    
    // Gerçek hesaplama için şu aşamada sayfayı yeniden yükleyelim
    if (id) {
      setIsLoading(true);
      
      try {
        // Mevcut repair verilerini al
        const repairData = id.startsWith('temp-') 
          ? JSON.parse(localStorage.getItem(id))
          : JSON.parse(sessionStorage.getItem('currentRepairData'));
        
        if (repairData) {
          // Varsayılan malzeme durumlarını repairData'ya ekle
          repairData.defaultMaterialsStatus = defaultMaterials;
          
          // Hesaplamayı güncelle
          await calculateDetailedBreakdown(repairData);
        }
      } catch (error) {
        console.error("Error recalculating with updated defaults:", error);
      } finally {
        setIsLoading(false);
      }
    }
  }, [defaultMaterials, id]);
  
  // Get user info from localStorage
  const getUserInfoFromStorage = useCallback(() => {
    try {
      const userData = localStorage.getItem("user") ? JSON.parse(localStorage.getItem("user")) : null;
      const tempData = id && localStorage.getItem(id) ? JSON.parse(localStorage.getItem(id)) : null;
      
      if (tempData?.userName) {
        return tempData.userName;
      }
      
      if (userData) {
        const fullName = `${userData.name || ''} ${userData.surname || ''}`.trim();
        return fullName || userData.username || userData.email || t("maintenance.anonymousUser");
      }
      
      return t("maintenance.anonymousUser");
    } catch (error) {
      console.error("Error getting user info:", error);
      return t("maintenance.anonymousUser");
    }
  }, [id, t]);
  
  // Check login status and set user info
  useEffect(() => {
    const token = localStorage.getItem("token");
    const isTemp = id?.startsWith("temp-");
    
    // Force authentication check - only allow access if user is logged in
    if (!token) {
      console.log("User not authenticated, redirecting to login");
      // Store current path for redirect after login
      const currentPath = window.location.pathname + window.location.search;
      localStorage.setItem('redirectAfterLogin', currentPath);
      
      // If it's a temp repair, store the ID for later use
      if (isTemp) {
        localStorage.setItem('pendingRepairId', id);
      }
      
      navigate('/login');
      return;
    }
    
    // Set isLoggedIn based on token only
    setIsLoggedIn(!!token);
    setUserInfo(getUserInfoFromStorage());
    
    console.log("Login status:", { isTemp, hasToken: !!token });
  }, [id, getUserInfoFromStorage, navigate]);
  
  // Cleanup localStorage data related to calculations
  const clearCalculationStorage = useCallback(() => {
    // Define keys that should be preserved
    const preserveKeys = [
      'selectedRoom',
      'selectedMaterial',
      'temp-selectedMaterial',
      'roomValues',
      'selectedPropertyType',
      'selectedPropertyStatuses',
      'selectedRepairStyle',
      'selectedRepairTariff',
      'homeData',
      id // Preserve current calculation
    ];
    
    // Only remove temporary calculation data
    Object.keys(localStorage).forEach((key) => {
      if ((key.startsWith('temp-') || key === "pendingRepairId") && !preserveKeys.includes(key)) {
        localStorage.removeItem(key);
      }
    });
    
    console.log("Storage cleanup completed. Preserved keys:", preserveKeys.filter(key => localStorage.getItem(key)));
  }, [id]);
  
  // Handle back button click
  const handleBackwardNavigation = useCallback(() => {
    clearCalculationStorage();
    navigate(backwardNavigate);
  }, [clearCalculationStorage, navigate, backwardNavigate]);

  // Cleanup on component unmount
  useEffect(() => {
    return () => {
      const timeoutId = setTimeout(() => {
        clearCalculationStorage();
      }, 100);
      
      return () => clearTimeout(timeoutId);
    };
  }, [clearCalculationStorage]);
  
  // Cleanup on component mount
  useEffect(() => {
    clearCalculationStorage();
  }, [clearCalculationStorage]);
  
  // Function to check if room data exists
  const checkRoomData = useCallback(() => {
    try {
      const roomValues = sessionStorage.getItem("roomValues");
      
      if (!roomValues || JSON.parse(roomValues).length === 0) {
        navigate("/dimensionroom");
        return false;
      }
      
      return true;
    } catch (error) {
      return false;
    }
  }, [navigate]);

  // Load repair data on component mount
  useEffect(() => {
    if (!checkRoomData()) {
      return;
    }
    
    const loadRepairData = async () => {
      try {
        setIsLoading(true);
        
        // Check if ID is a breakdown ID
        if (id && id.startsWith('breakdown-')) {
          // Extract the actual breakdown ID
          const breakdownId = id.replace('breakdown-', '');
          console.log("Loading breakdown data for ID:", breakdownId);
          
          // Import the getBreakdown service
          const { getBreakdown } = await import('../../services/repairService');
          
          // Fetch breakdown data
          const response = await getBreakdown(breakdownId);
          console.log("Breakdown API response:", response);
          
          if (response.success && response.data) {
            const breakdownData = response.data;
            const breakdownDetails = breakdownData.breakdown || breakdownData;
            const costs = breakdownData.costs || {};
            
            // Fetch material details to get store information
            await fetchMaterialDetails(breakdownDetails);
            
            // Update material price state
            setMaterialPrice({
              materialsCost: costs.materialsCost || 0,
              serviceCost: costs.serviceCost || 0,
              generalCost: costs.generalCost || 0,
              doorMaterialsCost: costs.doorMaterialsCost || 0,
              windowMaterialsCost: costs.windowMaterialsCost || 0,
              roomMaterialsCost: costs.roomMaterialsCost || 0,
              doorServiceCost: costs.doorServiceCost || 0,
              windowServiceCost: costs.windowServiceCost || 0,
              roomServiceCost: costs.roomServiceCost || 0,
              dependentMaterialsCost: costs.dependentMaterialsCost || 0,
              dependentServiceCost: costs.dependentServiceCost || 0,
              globalDefaultMaterialsCost: costs.globalDefaultMaterialsCost || 0,
              globalDefaultServiceCost: costs.globalDefaultServiceCost || 0,
              currency: costs.currency || "AZN",
              doorCount: breakdownData.property?.totalDoorCount || 0,
              windowCount: breakdownData.property?.totalWindowCount || 0,
              userInfo: breakdownData.userProfile?.userName || t("maintenance.anonymousUser"),
              isBreakdown: true,
              isTemporary: false
            });
            
            // Set breakdown data
            setBreakdown(breakdownDetails);
          } else {
            console.error("Failed to load breakdown data:", response);
            throw new Error('Failed to load breakdown data');
          }
        } else {
          // Handle regular repair data (temp or current)
          const repairData = id?.startsWith('temp-') 
            ? JSON.parse(localStorage.getItem(id))
            : JSON.parse(sessionStorage.getItem('currentRepairData'));

          if (!repairData) {
            console.error('No repair data found');
            navigate('/dimensionroom');
            return;
          }
          
          // Check if the repairData already has costs from Material component
          if (repairData.costs) {
            console.log('Using existing costs from repairData:', repairData.costs);
            
            // If we have breakdown data, use it directly
            if (repairData.breakdown) {
              console.log('Using existing breakdown from repairData:', repairData.breakdown);
              setBreakdown(repairData.breakdown);
              
              // Fetch material details to get store information
        await fetchMaterialDetails({ breakdown: repairData.breakdown });
              
                        // Check if fixtures costs are missing and fix them
          let updatedCosts = { ...repairData.costs };
          let updatedBreakdown = { ...repairData.breakdown };
          
          if ((!repairData.costs.doorMaterialsCost && !repairData.costs.doorServiceCost) || 
              (!repairData.costs.windowMaterialsCost && !repairData.costs.windowServiceCost)) {
            
            console.log('Fixtures costs missing from stored data, calculating manually...');
            
                          const homeData = JSON.parse(sessionStorage.getItem("homeData") || '{}');
              
              // Get counts and prices
              const doorCount = homeData.roomDoorCount || repairData.homeData?.totalDoorCount || 0;
              const windowCount = homeData.windowCount || repairData.homeData?.totalWindowCount || 0;
              
              // Fetch door and window prices from settings API instead of using hardcoded values
              const doorWindowPrices = await fetchDoorWindowPrices(repairData);
              const doorPrice = doorWindowPrices.doorPrice;
              const doorServicePrice = doorWindowPrices.doorServicePrice;
              const windowPrice = doorWindowPrices.windowPrice;
              const windowServicePrice = doorWindowPrices.windowServicePrice;
            
            // Calculate costs
            const fixtureDoorMaterialCost = doorPrice * doorCount;
            const fixtureDoorServiceCost = doorServicePrice * doorCount;
            const fixtureWindowMaterialCost = windowPrice * windowCount;
            const fixtureWindowServiceCost = windowServicePrice * windowCount;
            
            console.log('Calculated fixture costs for stored data:', {
              doorCount, windowCount,
              doorPrice, doorServicePrice, windowPrice, windowServicePrice,
              fixtureDoorMaterialCost, fixtureDoorServiceCost,
              fixtureWindowMaterialCost, fixtureWindowServiceCost
            });
            
            // Update fixtures in breakdown
            if (!updatedBreakdown.fixtures) {
              updatedBreakdown.fixtures = {};
            }
            
            updatedBreakdown.fixtures.doors = {
              count: doorCount,
              price: doorPrice,
              servicePrice: doorServicePrice,
              materialCost: fixtureDoorMaterialCost,
              serviceCost: fixtureDoorServiceCost,
              name: "Doors"
            };
            
            updatedBreakdown.fixtures.windows = {
              count: windowCount,
              price: windowPrice,
              servicePrice: windowServicePrice,
              materialCost: fixtureWindowMaterialCost,
              serviceCost: fixtureWindowServiceCost,
              name: "Windows"
            };
            
            // Update costs
            updatedCosts.doorMaterialsCost = fixtureDoorMaterialCost;
            updatedCosts.doorServiceCost = fixtureDoorServiceCost;
            updatedCosts.windowMaterialsCost = fixtureWindowMaterialCost;
            updatedCosts.windowServiceCost = fixtureWindowServiceCost;
            
            // Recalculate total costs
            updatedCosts.materialsCost = (updatedCosts.roomMaterialsCost || 0) + 
                                       (updatedCosts.dependentMaterialsCost || 0) + 
                                       fixtureDoorMaterialCost + 
                                       fixtureWindowMaterialCost +
                                       (updatedCosts.globalDefaultMaterialsCost || 0);
                                       
            updatedCosts.serviceCost = (updatedCosts.roomServiceCost || 0) + 
                                     (updatedCosts.dependentServiceCost || 0) + 
                                     fixtureDoorServiceCost + 
                                     fixtureWindowServiceCost +
                                     (updatedCosts.globalDefaultServiceCost || 0);
                                     
            updatedCosts.generalCost = updatedCosts.materialsCost + updatedCosts.serviceCost;
            
            // Update breakdown state
            setBreakdown(updatedBreakdown);
          }
              
              // Set material price directly from the stored costs
              setMaterialPrice({
            materialsCost: updatedCosts.materialsCost || 0,
            serviceCost: updatedCosts.serviceCost || 0,
            generalCost: updatedCosts.generalCost || 0,
            doorMaterialsCost: updatedCosts.doorMaterialsCost || 0,
            windowMaterialsCost: updatedCosts.windowMaterialsCost || 0,
            roomMaterialsCost: updatedCosts.roomMaterialsCost || 0,
            doorServiceCost: updatedCosts.doorServiceCost || 0,
            windowServiceCost: updatedCosts.windowServiceCost || 0,
            roomServiceCost: updatedCosts.roomServiceCost || 0,
            dependentMaterialsCost: updatedCosts.dependentMaterialsCost || 0,
            dependentServiceCost: updatedCosts.dependentServiceCost || 0,
            globalDefaultMaterialsCost: updatedCosts.globalDefaultMaterialsCost || 0,
            globalDefaultServiceCost: updatedCosts.globalDefaultServiceCost || 0,
            currency: updatedCosts.currency || "AZN",
                doorCount: repairData.homeData?.totalDoorCount || 0,
                windowCount: repairData.homeData?.totalWindowCount || 0,
                user: repairData.user || null,
                userInfo: repairData.user?.userName || t("maintenance.anonymousUser"),
                isTemporary: id?.startsWith('temp-') || false
              });
            } else {
              // Calculate detailed breakdown
              await calculateDetailedBreakdown(repairData);
            }
          } else {
            // Calculate detailed breakdown
            await calculateDetailedBreakdown(repairData);
          }
          
          setIsTemporary(id?.startsWith('temp-') || false);
        }

        // Restore selectedMaterial from sessionStorage
        const savedMaterial = sessionStorage.getItem('selectedMaterial');
        if (savedMaterial) {
          setSelectedMaterial(JSON.parse(savedMaterial));
        }
      } catch (error) {
        console.error("Error loading repair data:", error);
        navigate("/dimensionroom");
      } finally {
        setIsLoading(false);
      }
    };
    
    loadRepairData();
  }, [id, navigate, t, checkRoomData, calculateDetailedBreakdown]);

  // Get confirmation before resetting rooms
  const clearSessionRoomData = () => {
    if (window.confirm(t("maintenance.roomResetConfirm"))) {
      sessionStorage.removeItem("roomValues");
      navigate("/dimensionroom");
    }
  };

  // Handle view details button click
  const handleViewDetailsClick = () => {
      navigate(`/allmaterials/${id}`);
  };
  
  // Handle edit rooms button click
  const handleEditRoomsClick = () => {
    navigate("/dimensionroom");
  };
  
  // Property data from session storage
  const selectedPropertyType = JSON.parse(
    sessionStorage.getItem("selectedPropertyType")
  );
  const selectedHomeData = JSON.parse(sessionStorage.getItem("homeData"));
  
  // Handle login redirect
  const handleLoginRedirect = () => {
    try {
      if (id && id.startsWith('temp-')) {
        localStorage.setItem('pendingRepairId', id);
        console.log("Storing temp repair ID before login:", id);
        
        const tempData = localStorage.getItem(id);
        if (tempData) {
          localStorage.setItem('lastRepairData', tempData);
          
          const parsedData = JSON.parse(tempData);
          
          if (parsedData.property) {
            localStorage.setItem('lastRepairProperty', JSON.stringify(parsedData.property));
          }
          
          if (parsedData.materials) {
            localStorage.setItem('lastRepairMaterials', JSON.stringify(parsedData.materials));
          }
          
          localStorage.setItem('lastRepairMaterialCost', parsedData.materialsCost || 0);
          localStorage.setItem('lastRepairServiceCost', parsedData.serviceCost || 0);
          localStorage.setItem('lastRepairGeneralCost', parsedData.generalCost || 0);
        }
      }
    } catch (e) {
      console.error("Error saving repair data before login:", e);
    }
    
    const currentPath = window.location.pathname;
    navigate('/login', { state: { returnUrl: currentPath } });
  };

  // Save temporary repair to database
  const handleSaveToDatabase = async () => {
    if (!id || !id.startsWith('temp-')) return;
    
    try {
      // Check if user is logged in
      const token = localStorage.getItem('token');
      
      if (!token) {
        // Ask user if they want to log in to save
        if (window.confirm(t('maintenance.loginToSave'))) {
          // Store that we want to redirect to AllMaterials after login
          localStorage.setItem('redirectToAllMaterials', 'true');
          localStorage.setItem('pendingRepairId', id);
          
          // Redirect to login
          handleLoginRedirect();
        }
        return;
      }
      
      setIsLoading(true);
      
      const tempData = localStorage.getItem(id);
      if (!tempData) {
        throw new Error('No temporary data found');
      }
      
      const parsedData = JSON.parse(tempData);
      
      // Ensure we have a complete calculationData structure
      let calculationData;
      
      // Check if we already have a complete breakdown from the state
      if (breakdown && materialPrice) {
        // We have a complete breakdown calculation in the component state
        calculationData = {
          status: "success",
          costs: {
            materialsCost: materialPrice.materialsCost || 0,
            serviceCost: materialPrice.serviceCost || 0,
            generalCost: materialPrice.generalCost || 0,
            doorMaterialsCost: materialPrice.doorMaterialsCost || 0,
            windowMaterialsCost: materialPrice.windowMaterialsCost || 0,
            roomMaterialsCost: materialPrice.roomMaterialsCost || 0,
            doorServiceCost: materialPrice.doorServiceCost || 0,
            windowServiceCost: materialPrice.windowServiceCost || 0,
            roomServiceCost: materialPrice.roomServiceCost || 0,
            dependentMaterialsCost: materialPrice.dependentMaterialsCost || 0,
            dependentServiceCost: materialPrice.dependentServiceCost || 0,
            currency: materialPrice.currency || "AZN"
          },
          breakdown: breakdown
        };
      } else if (parsedData.costs && parsedData.breakdown) {
        // We have breakdown data in the parsed data
        calculationData = {
          status: "success",
          costs: parsedData.costs,
          breakdown: parsedData.breakdown
        };
      } else {
        // We need to recalculate the breakdown
                  try {
            console.log("No complete breakdown data found, recalculating...");
            const response = await axiosWithAuth().post(
              getApiEndpoint('repairs/calculate-breakdown'), 
              parsedData
            );
          calculationData = response.data;
        } catch (error) {
          console.error("Error recalculating breakdown:", error);
          throw new Error("Failed to calculate breakdown: " + error.message);
        }
      }
      
      // Validate the data structure
      console.log('Saving breakdown with direct save method...');
      if (!calculationData || !calculationData.costs || !calculationData.breakdown) {
        throw new Error("Invalid calculation data structure");
      }
      
      // Log what we're about to save
      console.log('Calculation data to save:', {
        hasCosts: !!calculationData.costs,
        materialsCost: calculationData.costs.materialsCost,
        roomMaterialsCost: calculationData.costs.roomMaterialsCost,
        dependentMaterialsCost: calculationData.costs.dependentMaterialsCost,
        hasBreakdown: !!calculationData.breakdown,
        roomsCount: calculationData.breakdown.rooms?.length || 0,
        hasFixtures: !!calculationData.breakdown.fixtures
      });
      
      // Extract and validate parameters
      let styleId = parsedData.repairStyle?.id || parsedData.repairStyle;
      let tariffId = parsedData.repairTariff?.id || parsedData.repairTariff;
      let propertyId = typeof parsedData.property === 'object' 
        ? (parsedData.property?._id || parsedData.property?.id || '') 
        : parsedData.property;
        
      console.log('Save parameters:', { 
        styleId, 
        tariffId, 
        propertyId,
        hasToken: !!token,
        propertyType: typeof parsedData.property,
        propertyKeys: typeof parsedData.property === 'object' ? Object.keys(parsedData.property) : 'N/A'
      });
      
      // If missing parameters, try to get from sessionStorage
      if (!styleId || !tariffId || !propertyId) {
        // Get from sessionStorage if missing in localStorage
        const sessionData = JSON.parse(sessionStorage.getItem('currentRepairData') || '{}');
        console.log('Trying sessionStorage data:', {
          hasSessionData: !!sessionData,
          sessionStyleId: sessionData.repairStyle?.id || sessionData.repairStyle,
          sessionTariffId: sessionData.repairTariff?.id || sessionData.repairTariff,
          sessionPropertyId: typeof sessionData.property === 'object' ? 
            (sessionData.property?._id || sessionData.property?.id) : sessionData.property,
          sessionPropertyType: typeof sessionData.property,
          sessionPropertyKeys: typeof sessionData.property === 'object' ? Object.keys(sessionData.property) : 'N/A'
        });
        
        // Update with session data as fallback
        styleId = styleId || sessionData.repairStyle?.id || sessionData.repairStyle;
        tariffId = tariffId || sessionData.repairTariff?.id || sessionData.repairTariff;
        propertyId = propertyId || (typeof sessionData.property === 'object' ? 
          (sessionData.property?._id || sessionData.property?.id) : sessionData.property);
        
        // Check selected property type from sessionStorage as another fallback
        if (!propertyId) {
          const selectedProperty = JSON.parse(sessionStorage.getItem('selectedPropertyType') || '{}');
          if (selectedProperty && (selectedProperty._id || selectedProperty.id)) {
            propertyId = selectedProperty._id || selectedProperty.id;
            console.log('Found property ID in selectedPropertyType:', propertyId);
          }
        }
        
        // Create a temporary property ID as last resort if we can't find one
        if (!propertyId) {
          // Use a timestamp-based ID with prefix if we can't find a propertyId
          propertyId = `temp-prop-${Date.now()}`;
          console.log('Created temporary property ID:', propertyId);
        }
        
        console.log('Final parameters:', { styleId, tariffId, propertyId });
      }
      
      // Final check for required parameters
      if (!styleId || !tariffId || !propertyId) {
        throw new Error(`Missing required parameters: ${!styleId ? 'styleId ' : ''}${!tariffId ? 'tariffId ' : ''}${!propertyId ? 'propertyId' : ''}`);
      }
      
      // Use our direct save utility
      const savedResult = await DirectBreakdownSave.saveDirectly(
        calculationData,
        {
          styleId,
          tariffId,
          propertyId,
          token
        }
      );
      
      console.log('Repair saved with direct method:', savedResult);
      
      alert(t('maintenance.savedToDatabase'));
      
      localStorage.removeItem(id);
      
      if (savedResult?.data?._id) {
        // Redirect to AllMaterials page with the ID
        navigate(`/allmaterials/${savedResult.data._id}`);
      } else {
        window.location.reload();
      }
    } catch (error) {
      console.error('Error saving repair to database:', error);
      alert(t('maintenance.errorSavingToDatabase') + ': ' + (error.message || error));
    } finally {
      setIsLoading(false);
    }
  };

  // Effect to check redirectParam
  useEffect(() => {
    if (redirectParam === 'allmaterials' && id && id.startsWith('temp-')) {
      // Automatically trigger save to database if we have a temp ID and redirect parameter
      const token = localStorage.getItem('token');
      if (token) {
        handleSaveToDatabase();
      }
    }
  }, [redirectParam, id]);

  // Check and upgrade localStorage data to ensure it has proper structure
  const checkAndUpgradeStorageData = useCallback(() => {
    if (!id || !id.startsWith('temp-')) return;
    
    try {
      const storageData = localStorage.getItem(id);
      if (!storageData) return;
      
      const parsedData = JSON.parse(storageData);
      
      // Check if the data already has the proper structure
      if (
        parsedData.costs?.materialsCost !== undefined &&
        parsedData.costs?.serviceCost !== undefined &&
        parsedData.costs?.dependentMaterialsCost !== undefined &&
        parsedData.breakdown?.rooms?.length > 0 &&
        parsedData.breakdown?.fixtures?.doors
      ) {
        console.log('Storage data already has proper structure');
        return;
      }
      
      console.log('Upgrading storage data structure...');
      
      // Build a proper costs structure if missing
      if (!parsedData.costs || typeof parsedData.costs !== 'object') {
        parsedData.costs = {
          materialsCost: parsedData.materialsCost || 0,
          serviceCost: parsedData.serviceCost || 0,
          generalCost: parsedData.generalCost || 0,
          currency: 'AZN',

        };
      }
      
      // Build a proper breakdown structure if missing
      if (!parsedData.breakdown || typeof parsedData.breakdown !== 'object') {
        parsedData.breakdown = {
          rooms: [],
          fixtures: {
            doors: {
              count: parsedData.property?.totalDoorCount || 0,
              materialCost: 0,
              serviceCost: 0,
              price: 0,
              servicePrice: 0
            },
            windows: {
              count: parsedData.property?.totalWindowCount || 0,
              materialCost: 0,
              serviceCost: 0,
              price: 0,
              servicePrice: 0
            }
          }
        };
      }
      
      // Save the upgraded data
      localStorage.setItem(id, JSON.stringify(parsedData));
      console.log('Storage data structure upgraded');
    } catch (error) {
      console.error('Error upgrading storage data:', error);
    }
  }, [id]);
  
  // Call the check and upgrade function when component mounts
  useEffect(() => {
    checkAndUpgradeStorageData();
  }, [checkAndUpgradeStorageData]);

  // Render door/window costs section
  const renderDoorWindowSection = () => {
    if (!breakdown?.fixtures) return null;
    
    const { doors, windows } = breakdown.fixtures;
    
    // If no doors or windows, don't render this section
    if ((!doors || doors.count === 0) && (!windows || windows.count === 0)) {
      return null;
    }

    return (
      <div className="maintenance-section mb-3">
        <h6>{t("maintenance.fixtureCosts")}</h6>
        
        {/* Doors */}
        {doors && doors.count > 0 && (
          <div className="maintenance-item">
            <span>{typeof doors.name === 'object' ? doors.name.az || doors.name.text || t("maintenance.doors") : (doors.name || t("maintenance.doors"))} ({doors.count})</span>
            <div>
              <div>{t("maintenance.materials")}: {doors.materialCost.toFixed(2)} {materialPrice?.currency || "AZN"}</div>
              <div>{t("maintenance.service")}: {doors.serviceCost.toFixed(2)} {materialPrice?.currency || "AZN"}</div>
            </div>
          </div>
        )}
        
        {/* Windows */}
        {windows && windows.count > 0 && (
          <div className="maintenance-item">
            <span>{typeof windows.name === 'object' ? windows.name.az || windows.name.text || t("maintenance.windows") : (windows.name || t("maintenance.windows"))} ({windows.count})</span>
            <div>
              <div>{t("maintenance.materials")}: {windows.materialCost.toFixed(2)} {materialPrice?.currency || "AZN"}</div>
              <div>{t("maintenance.service")}: {windows.serviceCost.toFixed(2)} {materialPrice?.currency || "AZN"}</div>
            </div>
          </div>
        )}
      </div>
    );
  };
  
  // Render room costs using breakdown data
  const renderRoomSection = () => {
    if (!breakdown?.rooms || breakdown.rooms.length === 0) return null;
    
    return (
      <>
        {/* Odalar ve Malzemeler */}
        {breakdown.rooms.map((room, index) => {
          // Calculate totals per room for validation
          const roomId = room.id || room._id;
          const roomTotal = roomTotals[roomId] || { 
            materialCost: room.materialCost + (room.dependentMaterialCost || 0),
            serviceCost: room.serviceCost + (room.dependentServiceCost || 0),
            totalCost: (room.materialCost + (room.dependentMaterialCost || 0)) + (room.serviceCost + (room.dependentServiceCost || 0))
          };
          const totalRoomCost = roomTotal.totalCost;
          
          // Skip rooms with no materials
          if (!room.materials || room.materials.length === 0) {
            return null;
          }
          
          // Check if room is expanded
const isRoomExpanded = expandedRooms[room.id] === true; // Default to collapsed if not set

// Count selected materials in this room
const selectedMaterialsCount = room.materials?.filter(material => {
  return material.status !== false && 
         material.isActive !== false &&
         selectedMaterials[material.materialId];
}).length || 0;

// Count total materials in this room
const totalMaterialsCount = room.materials?.filter(material => {
  return material.status !== false && material.isActive !== false;
}).length || 0;

// Determine if all materials are selected
const allMaterialsSelected = selectedMaterialsCount === totalMaterialsCount && totalMaterialsCount > 0;
    
    return (
  <div key={index} className="maintenance-section mb-3">
            <div className="d-flex align-items-center justify-content-between">
      <h6 className="mb-0">{room.roomType || room.name || `Oda ${index + 1}`} ({room.area}m²)</h6>
      <div>
        {isRoomExpanded && (
          <>
              <button 
              className="btn btn-sm btn-outline-secondary me-2"
              onClick={() => toggleAllMaterialsInRoom(room, !allMaterialsSelected)}
              title={allMaterialsSelected ? t("maintenance.deselectAll") : t("maintenance.selectAll")}
            >
              <i className={`bi ${allMaterialsSelected ? 'bi-square' : 'bi-check-square'}`}></i>
              {' '}
              {selectedMaterialsCount}/{totalMaterialsCount}
            </button>
          </>
        )}
        <button 
          className="btn btn-sm btn-outline-secondary"
          onClick={() => toggleRoomExpansion(room.id)}
        >
          <i className={`bi ${isRoomExpanded ? 'bi-dash-circle' : 'bi-plus-circle'}`}></i>
              </button>
            </div>
                          </div>
              
              {isRoomExpanded && (
                <div className="room-materials-container">
              {/* Display main materials */}
              {room.materials?.filter(material => {
                // Filter out inactive or deleted materials
                return material.status !== false && material.isActive !== false;
              }).map((material, mIndex) => {
                // Skip door and window materials as they are shown separately
                if (material.materialId === 'door-material' || material.materialId === 'window-material') {
                  return null;
                }
                
                // Skip default materials as they are shown in their own section
                if (material.isDefault === true && !material.isDependentMaterial) {
                  return null;
                }
                
                // Calculate material costs based on room area (usagePerSquareMeter ile)
                const materialArea = parseFloat(material.area || 1);
                const useRoomArea = materialArea === 1;
                const count = parseFloat(material.count || 1);
                const totalArea = useRoomArea ? room.area * count : materialArea * count;
                    
                    // Use 1 as fallback only if usagePerSquareMeter is null, undefined, or NaN
                    let usagePerSquareMeter = parseFloat(material.usagePerSquareMeter);
                    if (isNaN(usagePerSquareMeter)) {
                      usagePerSquareMeter = 1;
                    }
                
                const materialPrice = parseFloat(material.price || 0);
                const servicePrice = parseFloat(material.servicePrice || 0);
                
                // Material cost: totalArea * usagePerSquareMeter * price
                const actualMaterialUsage = totalArea * usagePerSquareMeter;
                const materialCost = materialPrice * actualMaterialUsage;
                
                // Service cost: totalArea * servicePrice (no usagePerSquareMeter)
                const serviceCost = servicePrice * totalArea;

                return (
                      <div key={mIndex} className={`maintenance-material-item ${selectedMaterials[material.materialId] ? 'active' : ''}`}>
                    <div className="maintenance-item">
                          <div className="d-flex align-items-center">
                            <div className="form-check me-2">
                              <input 
                                className="form-check-input" 
                                type="checkbox" 
                                id={`material-${material.materialId}`}
                                checked={selectedMaterials[material.materialId] || false}
                                onChange={() => toggleMaterial(material.materialId, material, room)}
                              />
                            </div>
                      <span>{typeof material.name === 'object' ? material.name.az || material.name.text || JSON.stringify(material.name) : material.name}</span>
                          </div>
                      <div>
                        <div>{(materialCost + serviceCost).toFixed(2)} {materialPrice?.currency || "AZN"}</div>
                        <div style={{fontSize: '0.8em', color: '#666', marginTop: '2px'}}>
                          {actualMaterialUsage.toFixed(2)}m²
                        </div>
                      </div>
                    </div>
                    
                    {/* Display dependent materials if any */}
                    {material.dependentMaterials?.filter(depMaterial => {
                      // Filter out inactive or deleted dependent materials
                      return depMaterial.status !== false && depMaterial.isActive !== false;
                    }).map((depMaterial, dIndex) => {
                      // Skip default dependent materials as they are shown in their own section
                      if (depMaterial.isDefault === true) {
                        return null;
                      }
                      
                      // Skip global default materials as they are shown in their own section
                      if (depMaterial.isGlobalDefault === true) {
                        return null;
                      }
                      
                      // Calculate dependent material costs based on the same area (usagePerSquareMeter ile)
                      const depPrice = parseFloat(depMaterial.price || 0);
                      const depServicePrice = parseFloat(depMaterial.servicePrice || 0);
                          
                          // Use 1 as fallback only if usagePerSquareMeter is null, undefined, or NaN
                          let depUsagePerSquareMeter = parseFloat(depMaterial.usagePerSquareMeter);
                          if (isNaN(depUsagePerSquareMeter)) {
                            depUsagePerSquareMeter = 1;
                          }
                      
                      // Dependent material cost: totalArea * usagePerSquareMeter * price
                      const depActualMaterialUsage = totalArea * depUsagePerSquareMeter;
                      const depMaterialCost = depPrice * depActualMaterialUsage;
                      
                      // Dependent service cost: totalArea * servicePrice (no usagePerSquareMeter)
                      const depServiceCost = depServicePrice * totalArea;
                      
                      return (
                            <div key={dIndex} className={`maintenance-dependent-item ${selectedMaterials[depMaterial.materialId] ? 'active' : ''}`}>
                              <div className="d-flex align-items-center">
                                <div className="form-check me-2">
                                  <input 
                                    className="form-check-input" 
                                    type="checkbox" 
                                    id={`dependent-material-${depMaterial.materialId}`}
                                    checked={selectedMaterials[depMaterial.materialId] || false}
                                    onChange={() => toggleMaterial(depMaterial.materialId, depMaterial, room)}
                                  />
                                </div>
                          <span>- {typeof depMaterial.name === 'object' ? (depMaterial.name?.az || depMaterial.name?.text || JSON.stringify(depMaterial.name)) : depMaterial.name}</span>
                              </div>
                          <div>
                            <div>{(depMaterialCost + depServiceCost).toFixed(2)} {materialPrice?.currency || "AZN"}</div>
                            <div style={{fontSize: '0.8em', color: '#666', marginTop: '2px'}}>
                              {depActualMaterialUsage.toFixed(2)}m²
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                );
              })}
              
                </div>
              )}
            </div>
          );
        })}
      </>
    );
  };

  // Calculate initial totals based on selected materials
  useEffect(() => {
    if (breakdown?.rooms && Object.keys(selectedMaterials).length > 0) {
      let totalMaterialsCost = 0;
      let totalServiceCost = 0;
      
      // First, add door and window costs from fixtures
      if (breakdown.fixtures) {
        const { doors, windows } = breakdown.fixtures;
        
        // Add door costs if selected
        if (doors && doors.count > 0) {
          totalMaterialsCost += parseFloat(doors.materialCost || 0);
          totalServiceCost += parseFloat(doors.serviceCost || 0);
        }
        
        // Add window costs if selected
        if (windows && windows.count > 0) {
          totalMaterialsCost += parseFloat(windows.materialCost || 0);
          totalServiceCost += parseFloat(windows.serviceCost || 0);
        }
      }
      
      // Then process room materials
      breakdown.rooms.forEach(room => {
        room.materials?.forEach(material => {
          // Skip if material is inactive
          if (material.status === false || material.isActive === false) {
            return;
          }
          
          // Skip door and window materials as they are already counted in fixtures
          if (material.materialId === 'door-material' || material.materialId === 'window-material') {
            return;
          }
          
          if (selectedMaterials[material.materialId]) {
            // Calculate material costs
            const materialArea = parseFloat(material.area || 1);
            const useRoomArea = materialArea === 1;
            const count = parseFloat(material.count || 1);
            const totalArea = useRoomArea ? parseFloat(room.area) * count : materialArea * count;
            
            // Use 1 as fallback only if usagePerSquareMeter is null, undefined, or NaN
            let usagePerSquareMeter = parseFloat(material.usagePerSquareMeter);
            if (isNaN(usagePerSquareMeter)) {
              usagePerSquareMeter = 1;
            }
            
            const materialPrice = parseFloat(material.price || 0);
            const servicePrice = parseFloat(material.servicePrice || 0);
            
            // Material cost: totalArea * usagePerSquareMeter * price
            const actualMaterialUsage = totalArea * usagePerSquareMeter;
            const materialCost = materialPrice * actualMaterialUsage;
            
            // Service cost: totalArea * servicePrice (no usagePerSquareMeter)
            const serviceCost = servicePrice * totalArea;
            
            totalMaterialsCost += materialCost;
            totalServiceCost += serviceCost;
            
            // Also include dependent materials when parent is selected
            material.dependentMaterials?.forEach(depMaterial => {
              // Skip if dependent material is inactive
              if (depMaterial.status === false || depMaterial.isActive === false) {
                return;
              }
              
              // Calculate dependent material costs
              const depPrice = parseFloat(depMaterial.price || 0);
              const depServicePrice = parseFloat(depMaterial.servicePrice || 0);
              
              // Use 1 as fallback only if usagePerSquareMeter is null, undefined, or NaN
              let depUsagePerSquareMeter = parseFloat(depMaterial.usagePerSquareMeter);
              if (isNaN(depUsagePerSquareMeter)) {
                depUsagePerSquareMeter = 1;
              }
              
              // Dependent material cost: totalArea * usagePerSquareMeter * price
              const depActualMaterialUsage = totalArea * depUsagePerSquareMeter;
              const depMaterialCost = depPrice * depActualMaterialUsage;
              
              // Dependent service cost: totalArea * servicePrice (no usagePerSquareMeter)
              const depServiceCost = depServicePrice * totalArea;
              
              totalMaterialsCost += depMaterialCost;
              totalServiceCost += depServiceCost;
            });
          }
          
          // Include individually selected dependent materials
          material.dependentMaterials?.forEach(depMaterial => {
            // Skip if dependent material is inactive
            if (depMaterial.status === false || depMaterial.isActive === false) {
              return;
            }
            
            // Only include if explicitly selected and parent is not selected
            if (selectedMaterials[depMaterial.materialId] && !selectedMaterials[material.materialId]) {
              const materialArea = parseFloat(material.area || 1);
              const useRoomArea = materialArea === 1;
              const count = parseFloat(material.count || 1);
              const totalArea = useRoomArea ? parseFloat(room.area) * count : materialArea * count;
              
              const depPrice = parseFloat(depMaterial.price || 0);
              const depServicePrice = parseFloat(depMaterial.servicePrice || 0);
              
              // Use 1 as fallback only if usagePerSquareMeter is null, undefined, or NaN
              let depUsagePerSquareMeter = parseFloat(depMaterial.usagePerSquareMeter);
              if (isNaN(depUsagePerSquareMeter)) {
                depUsagePerSquareMeter = 1;
              }
              
              // Dependent material cost: totalArea * usagePerSquareMeter * price
              const depActualMaterialUsage = totalArea * depUsagePerSquareMeter;
              const depMaterialCost = depPrice * depActualMaterialUsage;
              
              // Dependent service cost: totalArea * servicePrice (no usagePerSquareMeter)
              const depServiceCost = depServicePrice * totalArea;
              
              totalMaterialsCost += depMaterialCost;
              totalServiceCost += depServiceCost;
            }
          });
        });
      });
      
      setCalculatedTotals({
        materialsCost: totalMaterialsCost,
        serviceCost: totalServiceCost,
        generalCost: totalMaterialsCost + totalServiceCost
      });
    }
  }, [breakdown, selectedMaterials]);

  // Render total costs section
  const renderTotalSection = () => {
    // Use the calculatedTotals instead of materialPrice
    const totalMaterials = calculatedTotals.materialsCost || 0;
    const totalService = calculatedTotals.serviceCost || 0;
    const totalGeneral = calculatedTotals.generalCost || 0;
    
    return (
      <div className="maintenance-section mb-3">
        <div className="maintenance-item total-item">
          <span>{t("maintenance.materials")}</span>
                    <div>
            <div className="total-price">{totalMaterials.toFixed(2)} {materialPrice?.currency || "AZN"}</div>
                    </div>
                  </div>
              <div className="maintenance-item total-item">
          <span>{t("maintenance.service")}</span>
                <div>
            <div className="total-price">{totalService.toFixed(2)} {materialPrice?.currency || "AZN"}</div>
                    </div>
        </div>
        <div className="maintenance-item total-item">
          <span>{t("maintenance.total")}</span>
          <div>
            <div className="total-price">{totalGeneral.toFixed(2)} {materialPrice?.currency || "AZN"}</div>
                </div>
              </div>
            </div>
    );
  };

  // Toggle room expansion
  const toggleRoomExpansion = (roomId) => {
    setExpandedRooms(prev => {
      // If roomId doesn't exist in the state yet, default to true (expanded)
      const currentState = prev[roomId] !== undefined ? prev[roomId] : true;
      
      return {
        ...prev,
        [roomId]: !currentState
      };
    });
  };

  // Toggle all materials in a room
  const toggleAllMaterialsInRoom = (room, select) => {
    const newSelectedMaterials = { ...selectedMaterials };
    
    // Process all materials in the room
    room.materials?.forEach(material => {
      // Skip if material is inactive
      if (material.status === false || material.isActive === false) {
        return;
      }
      
      // Skip door and window materials
      if (material.materialId === 'door-material' || material.materialId === 'window-material') {
        return;
      }
      
      // Set selection state
      newSelectedMaterials[material.materialId] = select;
      
      // Also process dependent materials
      material.dependentMaterials?.forEach(depMaterial => {
        // Skip if dependent material is inactive
        if (depMaterial.status === false || depMaterial.isActive === false) {
          return;
        }
        
        // Set selection state
        newSelectedMaterials[depMaterial.materialId] = select;
      });
    });
    
    // Update selected materials state
    setSelectedMaterials(newSelectedMaterials);
    
    // Save to localStorage
    try {
      localStorage.setItem(SELECTED_MATERIALS_STORAGE_KEY, JSON.stringify(newSelectedMaterials));
    } catch (error) {
      console.error("Error saving selected materials status:", error);
    }
    
    // Recalculate totals
    recalculateAllTotals();
  };

  // Recalculate all totals based on current selected materials
  const recalculateAllTotals = () => {
    if (!breakdown?.rooms) return;
    
    let totalMaterialsCost = 0;
    let totalServiceCost = 0;
    
    // First, add door and window costs from fixtures
    if (breakdown.fixtures) {
      const { doors, windows } = breakdown.fixtures;
      
      // Add door costs
      if (doors && doors.count > 0) {
        totalMaterialsCost += parseFloat(doors.materialCost || 0);
        totalServiceCost += parseFloat(doors.serviceCost || 0);
      }
      
      // Add window costs
      if (windows && windows.count > 0) {
        totalMaterialsCost += parseFloat(windows.materialCost || 0);
        totalServiceCost += parseFloat(windows.serviceCost || 0);
      }
    }
    
    // Calculate room totals and store them for display
    const roomTotals = {};
    
    // Then process room materials
    breakdown.rooms.forEach(room => {
      let roomMaterialsCost = 0;
      let roomServiceCost = 0;
      
      room.materials?.forEach(material => {
        // Skip if material is inactive
        if (material.status === false || material.isActive === false) {
          return;
        }
        
        // Calculate material costs
        const materialArea = parseFloat(material.area || material.totalArea || 0);
        const materialPrice = parseFloat(material.price || 0);
        const servicePrice = parseFloat(material.servicePrice || 0);
        
        roomMaterialsCost += materialPrice * materialArea;
        roomServiceCost += servicePrice * materialArea;
      });
      
      // Store room totals
      roomTotals[room.id] = {
        materialCost: roomMaterialsCost,
        serviceCost: roomServiceCost,
        totalCost: roomMaterialsCost + roomServiceCost
      };
      
      // Add to overall totals
      totalMaterialsCost += roomMaterialsCost;
      totalServiceCost += roomServiceCost;
    });
    
    // Store the updated room totals for display
    setRoomTotals(roomTotals);
    
    // Update calculated totals
    setCalculatedTotals({
      materialsCost: totalMaterialsCost,
      serviceCost: totalServiceCost,
      generalCost: totalMaterialsCost + totalServiceCost
    });
  };  // Add semicolon here

  // Main render function
  return (
    <div className="maintenance-section">
      <Header
        title={headerTitle}
        showProfileIcon={true}
        showBackIcon={true}
        onBackClick={handleBackwardNavigation}
      />

      {isLoading ? (
        <div className="loading-container">
          <div className="spinner-border text-primary" role="status">
            <span className="visually-hidden">Loading...</span>
          </div>
        </div>
      ) : (
        <div className="container maintenance-content">
          {materialPrice ? (
            <>
              {/* Property information section */}
              {selectedPropertyType && selectedHomeData && (
                <div className="maintenance-wrap">
                  <div className="maintenance-info">
                    <div className="maintenance-infoDiv">
                      <span className="maintenance-label">{t("maintenance.propertyType")}:</span>
                      <span className="maintenance-price">{selectedPropertyType.name?.text || 'N/A'}</span>
                    </div>
                    <div className="maintenance-infoDiv">
                      <span className="maintenance-label">{t("maintenance.givenArea")}:</span>
                      <span className="maintenance-price">{selectedHomeData.areaHouse || 0} m²</span>
                    </div>
                    <div className="maintenance-infoDiv">
                      <span className="maintenance-label">{t("maintenance.calculatedArea")}:</span>
                      <span className="maintenance-price">
                        {(() => {
                          // First try to get total area from breakdown rooms data
                          if (breakdown?.rooms && breakdown.rooms.length > 0) {
                            const totalRoomArea = breakdown.rooms.reduce((total, room) => {
                              return total + (parseFloat(room.area) || 0);
                            }, 0);
                            if (totalRoomArea > 0) {
                              return totalRoomArea.toFixed(2);
                            }
                          }
                          
                          // Fallback to selectedRooms from sessionStorage
                          if (selectedRooms && selectedRooms.length > 0) {
                            const totalSelectedArea = selectedRooms.reduce((total, room) => {
                              return total + (parseFloat(room.area) || 0);
                            }, 0);
                            if (totalSelectedArea > 0) {
                              return totalSelectedArea.toFixed(2);
                            }
                          }
                          
                          // Fallback to 0 if no rooms selected
                          return "0";
                        })()} m²
                      </span>
                    </div>
                    <div className="maintenance-infoDiv">
                      <span className="maintenance-label">{t("calculator.3")}:</span>
                      <span className="maintenance-price">{selectedHomeData.heightWall || 0} m</span>
                    </div>
                  </div>
                </div>
              )}

              {/* Selected Rooms */}
              {selectedRooms && selectedRooms.length > 0 && (
                <div className="maintenance-wrap">
                  <h6 className="mb-3">{t("maintenance.calculatedRooms")} ({selectedRooms.length})</h6>
                  <div className="selected-rooms">
                    {selectedRooms.map((room, index) => (
                      <button
                        className="btn room-btn"
                        key={index}
                        onClick={handleEditRoomsClick}
                      >
                        {room.name}
                      </button>
                    ))}
                  </div>
                  <button 
                    className="btn btn-sm global-button-secondary mt-2" 
                    onClick={clearSessionRoomData}
                  >
                    <i className="bi bi-arrow-repeat me-1"></i> {t("maintenance.selectRoomsAgain")}
                  </button>
                </div>
              )}

              {/* Store Information */}
              {materialPrice && breakdown?.rooms && (
                <div className="maintenance-wrap">
                  <h6 className="mb-3">{t("maintenance.storeInfo") || "Mağaza Məlumatları"}</h6>
                  
                  {/* Store Information */}
                  <div className="store-info-section mb-3">
                    <div className="store-list" style={{ display: 'block' }}>
                      {(() => {
                        // Check if there are materials with store information
                        const allMaterials = [];
                        
                        // Collect all materials from rooms
                        if (breakdown?.rooms) {
                          breakdown.rooms.forEach(room => {
                            if (room.materials && room.materials.length > 0) {
                              room.materials.forEach(material => {
                                allMaterials.push(material);
                              });
                            }
                          });
                        }
                        
                        // Check if we have material details with store information
                        const materialDetailsAvailable = materialDetails && Object.keys(materialDetails).length > 0;
                        
                        // Prepare store array
                        const storeMap = new Map();
                        
                        // Process materials to extract store information
                        allMaterials.forEach(material => {
                          // Get room information for this material
                          const roomName = material.room?.name || material.roomType || "Unknown Room";
                          
                          // Process main material
                          const processMaterialStores = (mat, isDependent = false, parentName = null) => {
                            // Check if material has store information
                            if (mat.stores && mat.stores.length > 0) {
                              mat.stores.forEach(store => {
                                if (!storeMap.has(store.id)) {
                                  storeMap.set(store.id, {
                                    id: store.id,
                                    name: store.name?.text || store.name,
                                    materials: [],
                                    // Add rooms map to group materials by room
                                    rooms: new Map()
                                  });
                                }
                                
                                // Initialize room in store if needed
                                if (!storeMap.get(store.id).rooms.has(roomName)) {
                                  storeMap.get(store.id).rooms.set(roomName, []);
                                }
                                
                                // Create material object with room info
                                const materialObj = {
                                  id: mat.materialId,
                                  name: mat.name?.az || mat.name?.text || mat.name,
                                  price: mat.price,
                                  currency: mat.currency,
                                  roomName: roomName,
                                  isDependent: isDependent,
                                  parentName: parentName
                                };
                                
                                // Add material to store's general materials list
                                storeMap.get(store.id).materials.push(materialObj);
                                
                                // Add material to room-specific list
                                storeMap.get(store.id).rooms.get(roomName).push(materialObj);
                              });
                            }
                            
                            // If material doesn't have store information, check materialDetails
                            else if (materialDetails && materialDetails[mat.materialId]) {
                              const matDetail = materialDetails[mat.materialId];
                              
                              if (matDetail.stores && matDetail.stores.length > 0) {
                                matDetail.stores.forEach(store => {
                                  if (!storeMap.has(store.id)) {
                                    storeMap.set(store.id, {
                                      id: store.id,
                                      name: store.name?.text || store.name,
                                      materials: [],
                                      rooms: new Map()
                                    });
                                  }
                                  
                                  // Initialize room in store if needed
                                  if (!storeMap.get(store.id).rooms.has(roomName)) {
                                    storeMap.get(store.id).rooms.set(roomName, []);
                                  }
                                  
                                  // Create material object with room info
                                  const materialObj = {
                                    id: mat.materialId,
                                    name: mat.name?.az || mat.name?.text || mat.name,
                                    price: mat.price,
                                    currency: mat.currency,
                                    roomName: roomName,
                                    isDependent: isDependent,
                                    parentName: parentName
                                  };
                                  
                                  // Add material to store's general materials list
                                  storeMap.get(store.id).materials.push(materialObj);
                                  
                                  // Add material to room-specific list
                                  storeMap.get(store.id).rooms.get(roomName).push(materialObj);
                                });
                              }
                            }
                          };
                          
                          // Process main material
                          processMaterialStores(material, false);
                          
                          // Process dependent materials if any
                          if (material.dependentMaterials && Array.isArray(material.dependentMaterials)) {
                            const parentName = material.name?.az || material.name?.text || material.name;
                            material.dependentMaterials.forEach(depMaterial => {
                              processMaterialStores(depMaterial, true, parentName);
                            });
                          }
                          // Also check if we have material details with store info
                          else if (materialDetailsAvailable) {
                            const materialDetail = materialDetails[material.materialId];
                            
                            // Use room info from materialDetail if available
                            let materialRoomName = roomName;
                            if (materialDetail && materialDetail.roomInfo) {
                              materialRoomName = materialDetail.roomInfo.roomName || roomName;
                            }
                            
                            if (materialDetail && materialDetail.stores && materialDetail.stores.length > 0) {
                              materialDetail.stores.forEach(store => {
                                if (!storeMap.has(store.id)) {
                                  storeMap.set(store.id, {
                                    id: store.id,
                                    name: store.name?.text || store.name,
                                    materials: [],
                                    // Add rooms map to group materials by room
                                    rooms: new Map()
                                  });
                                }
                                
                                // Initialize room in store if needed
                                if (!storeMap.get(store.id).rooms.has(materialRoomName)) {
                                  storeMap.get(store.id).rooms.set(materialRoomName, []);
                                }
                                
                                // Create material object with room info
                                const materialObj = {
                                  id: material.materialId,
                                  name: material.name?.az || material.name?.text || material.name,
                                  price: material.price,
                                  currency: material.currency,
                                  roomName: materialRoomName
                                };
                                
                                // Add material to store's general materials list
                                storeMap.get(store.id).materials.push(materialObj);
                                
                                // Add material to room-specific list
                                storeMap.get(store.id).rooms.get(materialRoomName).push(materialObj);
                              });
                            }
                          }
                        });
                        
                        // Convert map to array
                        const storeArray = Array.from(storeMap.values());
                        
                        // Check if we have store information
                        const hasStoreInfo = storeArray.length > 0;
                        
                        // Render store information
                        if (storeArray.length === 0 || !hasStoreInfo) {
                          return (
                            <div className="alert alert-info">
                              <p>{t("maintenance.noStoreInfo") || "Mağaza məlumatları mövcud deyil"}</p>
                            </div>
                          );
                        }
                        
                        // Render store information
                        return storeArray.map((store, index) => (
                          <div key={store.id || index} className="store-item">
                            <div 
                              className="store-header" 
                              onClick={() => setExpandedStoreId(expandedStoreId === store.id ? null : store.id)}
                              style={{cursor: 'pointer'}}
                            >
                              <div className="store-info">
                                <h5 className="store-name">{store.name}</h5>
                                {store.address && <p className="store-address">{store.address}</p>}
                                <p className="store-material-count">
                                  {t("maintenance.materialCount") || "Material sayı"}: {store.materials.length}
                                </p>
                                {/* Show room count */}
                                <p className="store-room-count">
                                  {t("maintenance.roomCount") || "Otaq sayı"}: {store.rooms ? store.rooms.size : 0}
                                </p>
                              </div>
                              <div className="store-toggle">
                                <i className={`bi ${expandedStoreId === store.id ? 'bi-chevron-up' : 'bi-chevron-down'}`}></i>
                              </div>
                            </div>
                            
                            {expandedStoreId === store.id && (
                              <div className="store-material-details">
                                {/* Group materials by room */}
                                {store.rooms && Array.from(store.rooms.entries()).map(([roomName, materials]) => (
                                  <div key={roomName} className="room-materials mb-3">
                                    <h6 className="room-name mb-2">{roomName}</h6>
                                <div className="material-list">
                                      {materials.map((material, mIdx) => (
                                        <div 
                                          key={material.id || mIdx} 
                                          className={`material-item ${material.isDependent ? 'dependent-material' : ''}`}
                                        >
                                          <span className="material-name">
                                            {material.isDependent && (
                                              <span className="dependent-indicator">
                                                <i className="bi bi-arrow-return-right me-1"></i>
                                              </span>
                                            )}
                                            {material.name}
                                            {material.isDependent && material.parentName && (
                                              <small className="parent-material-name">
                                                ({t("maintenance.dependentOf") || "Asılı"}: {material.parentName})
                                              </small>
                                            )}
                                          </span>
                                      <span className="material-price float-end">
                                        {material.price} {material.currency || "AZN"}
                                      </span>
                                    </div>
                                  ))}
                                </div>
                                  </div>
                                ))}
                              </div>
                            )}
                            
                            {store.contact && (
                              <div className="store-contact mt-2">
                                <a 
                                  href={`tel:${store.contact}`} 
                                  className="btn btn-sm btn-outline-primary"
                                >
                                  <i className="bi bi-telephone me-1"></i>
                                  {t("maintenance.contactStore") || "Mağaza ilə əlaqə"}
                                </a>
                              </div>
                            )}
                          </div>
                        ));
                      })()}
                    </div>
                  </div>
                  

                </div>
              )}
            </>
          ) : (
            <div className="maintenance-no-data">
              <p>{t("maintenance.noData")}</p>
              <button
                className="btn py-3 global-button"
                onClick={() => navigate("/dimensionroom")}
              >
                {t("maintenance.startOver")}
              </button>
            </div>
          )}
          
          {/* Render Door/Window and Room Sections */}
          {renderDoorWindowSection()}
          {renderRoomSection()}
          {renderTotalSection()}

          {/* Additional Navigation Buttons */}
          <div className="maintenance-additional-buttons">
            <div className="mb-3">
                <button 
                  className="btn py-3 global-button-secondary w-100" 
                  onClick={() => navigate('/repairmen')}
                style={{ border: '1px solid #dee2e6' }}
                >
                  <i className="bi bi-people-fill me-2"></i>
                  {t("maintenance.viewRepairmen")}
                </button>
              </div>
            <div className="mb-3">
                <button 
                  className="btn py-3 global-button-secondary w-100" 
                  onClick={() => navigate('/dependent-materials')}
                style={{ border: '1px solid #dee2e6' }}
                >
                  <i className="bi bi-box-seam me-2"></i>
                  {t("maintenance.viewDependentMaterials")}
                </button>
              </div>
            </div>

          {/* Action Buttons */}
          <div className="maintenance-btnGroup">
            {isTemporary && (
              <button className="btn py-3 global-button" onClick={handleSaveToDatabase}>
                {t("maintenance.saveToDatabase")}
              </button>
            )}
            <button className="btn py-3 global-button" onClick={handleViewDetailsClick}>
              {t("maintenance.viewDetails")}
            </button>
            <button className="btn py-3 global-button" onClick={handleEditRoomsClick}>
              {t("maintenance.editRooms")}
            </button>
          </div>
          
          {/* Login Prompt if not logged in - show optional login button */}
          {!isLoggedIn && (
            <div className="maintenance-login-prompt">
              <p>{t("maintenance.temporaryNotSaved")}</p>
              <button
                className="btn py-2 global-button-secondary"
                onClick={handleLoginRedirect}
              >
                {t("maintenance.login")}
              </button>
            </div>
          )}
          
          {/* Add styling for room-based material grouping */}
          <style jsx="true">{`
            .room-materials {
              border: 1px solid #f0f0f0;
              border-radius: 8px;
              padding: 12px;
              background-color: #fafafa;
              margin-bottom: 15px;
            }
            
            .room-name {
              font-size: 16px;
              font-weight: 600;
              color: #333;
              border-bottom: 1px solid #eee;
              padding-bottom: 6px;
              margin-bottom: 10px;
              color: #7B7D51;
            }
            
            .store-material-details {
              padding: 10px;
              background-color: #fff;
              border-radius: 8px;
            }
            
            .material-list {
              display: flex;
              flex-direction: column;
              gap: 8px;
            }
            
            .material-item {
              padding: 8px;
              border-bottom: 1px solid #f5f5f5;
              display: flex;
              justify-content: space-between;
              align-items: center;
            }
            
            .material-item:last-child {
              border-bottom: none;
            }
            
            .dependent-material {
              padding-left: 20px;
              background-color: #f9f9f9;
              border-left: 3px solid #cda742;
            }
            
            .dependent-indicator {
              color: #cda742;
              margin-right: 5px;
            }
            
            .parent-material-name {
              display: block;
              font-size: 12px;
              color: #777;
              margin-top: 2px;
            }
            
            .store-room-count {
              font-size: 14px;
              color: #666;
              margin-bottom: 5px;
            }
            
            .store-header {
              background-color: #f9f9f9;
              border-radius: 8px;
              padding: 12px 15px;
              margin-bottom: 5px;
            }
            
            .store-item {
              margin-bottom: 15px;
              border: 1px solid #eee;
              border-radius: 8px;
              overflow: hidden;
            }
            
            /* Styles for selectable materials */
            .maintenance-material-item {
              opacity: 0.7;
              transition: opacity 0.2s ease;
            }
            
            .maintenance-material-item.active {
              opacity: 1;
            }
            
            .maintenance-dependent-item {
              opacity: 0.7;
              transition: opacity 0.2s ease;
            }
            
            .maintenance-dependent-item.active {
              opacity: 1;
            }
            
            /* Checkbox styles */
            .form-check-input {
              cursor: pointer;
            }
            
            /* Room expansion styles */
            .room-materials-container {
              margin-top: 10px;
              padding-top: 10px;
              border-top: 1px solid #eee;
            }
            
            .maintenance-section h6 {
              margin-bottom: 0;
            }
            
            .btn-outline-secondary {
              border-color: #e0e0e0;
              color: #666;
            }
            
            .btn-outline-secondary:hover {
              background-color: #f8f9fa;
              color: #333;
            }
            
            .dependent-summary {
              background-color: #f9f9f9;
              border-left: 3px solid #cda742;
              padding-left: 15px;
            }
            
            /* Price display styling */
            .maintenance-item > div:last-child {
              font-weight: 600;
              color: #7B7D51;
            }
          `}</style>
        </div>
      )}
    </div>
  );
};

export default Maintenance;